// AdminAnalysis — admin sub-view, extracted verbatim from App.js.
import { useState, useEffect, useMemo } from "react";
import { useT } from "../lib/i18n";
import { C } from "../lib/theme";
import { pct, posc } from "../lib/format";
import { Btn, Card, EmptyState, SectionHdr, InfoTip } from "../components/ui";
import { useTableSortFilter, SortableTh } from "../lib/tableSort";
import { MarkdownText } from "../components/MarkdownText";
import { calcDaily, calcItem, applyPeriodAdjustments, monthsForSpan } from "../lib/calc";
import { effNet, coFundAmount } from "../lib/coFund";
import { rangeWindow } from "../lib/ranges";
import { getCachedAnalysis, saveAnalysisToCache, computeDataFingerprint, computeMenuFingerprint } from "../data/aiCache";
import { getCurrentUser } from "../data/auth";
import { callAnthropic } from "../lib/api";
import { BarChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Pie } from "recharts";

// InfoTip (portal-based ⓘ tooltip) now lives in ../components/ui — imported above.

// Insights page title brand mark — trailing "+" rendered as a small raised
// superscript (~0.62em) so the H1 reads "Insight⁺". lineHeight:0 prevents it from
// nudging the 22px SectionHdr title's baseline/height; it inherits C.text via the
// parent so theme colours are unchanged.
const InsightPlusTitle = (
  <>Insight<sup style={{fontSize:"0.62em",verticalAlign:"super",lineHeight:0,fontWeight:"inherit",letterSpacing:0}}>+</sup></>
);

// When the operator's active language is Arabic (localStorage "netrev_lang" === "ar"),
// return a hard instruction telling the model to write its ENTIRE response in Arabic.
// Appended to the AI prompts below; returns "" for every other language so English
// behaviour is completely unchanged. Wrapped in try/catch — localStorage can throw
// in private-mode / SSR contexts and must never break analysis generation.
function arabicReplyInstruction() {
  try {
    if (typeof window !== "undefined" && window.localStorage &&
        window.localStorage.getItem("netrev_lang") === "ar") {
      return `

═══════════════════════════════════════
LANGUAGE: Write your ENTIRE response in Arabic (العربية). Every heading, sentence, label and explanation must be in Modern Standard Arabic. Keep all numbers, currency symbols and figures exactly as given. Do not add any English text or translations.`;
    }
  } catch (e) { /* localStorage unavailable — fall through to English */ }
  return "";
}

function formatRelativeTime(ts) {
  if (!ts) return "";
  const date = ts instanceof Date ? ts : new Date(ts);
  if (isNaN(date.getTime())) return "";
  const diff = Date.now() - date.getTime();
  const min = Math.floor(diff / 60000);
  const hr  = Math.floor(min / 60);
  const day = Math.floor(hr / 24);
  if (min < 1)  return "just now";
  if (min < 60) return `${min} min ago`;
  if (hr  < 24) return `${hr} hour${hr === 1 ? '' : 's'} ago`;
  if (day < 7)  return `${day} day${day === 1 ? '' : 's'} ago`;
  return date.toLocaleDateString("en-GB", {day: "2-digit", month: "short"});
}

function generateInsightsPDF({ wsName, period, currency, sym, totals, perPlatform, aiText, profitPctOfGross, contribPct, avgDiscountPct, receivablePctOfGross, cpcPctOfNet, vatRate, cogsPct, coverageRatio = 0, trueProfitAfterFixed = 0, hasFixedCosts = false }) {
  const printWin = window.open("", "_blank", "width=900,height=1100");
  if (!printWin) {
    alert("Please allow popups for this site to download the PDF.");
    return;
  }

  const fmtCurrency = (n) => `${sym}${(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const today = new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });
  const reportRef = `GS-${Date.now().toString(36).toUpperCase().slice(-6)}`;

  // Determine verdict for executive summary — coverage-led: a business that
  // covers its fixed costs is "Healthy" even on a thin delivery margin. Margin %
  // is shown as context, not used to label risk. Falls back to true profit when
  // no fixed costs are configured (coverage can't be computed).
  let verdict = "Healthy";
  let verdictColor = "#0f7048";
  if (totals.profit < 0) { verdict = "Loss-making"; verdictColor = "#a52727"; }
  else if (hasFixedCosts && trueProfitAfterFixed < 0) { verdict = "Below break-even"; verdictColor = "#a52727"; }
  else if (hasFixedCosts && coverageRatio < 1.2) { verdict = "Profitable but fragile"; verdictColor = "#9a6a00"; }
  else if (hasFixedCosts && coverageRatio >= 2) { verdict = "Excellent"; verdictColor = "#0f7048"; }

  // Convert AI markdown to clean HTML
  const aiHtml = (aiText || "")
    .split(/\n\s*\n/)
    .map(block => {
      const t = block.trim();
      if (!t) return "";
      if (t.startsWith("## ")) return `<h3 class="ai-h3">${t.slice(3).replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")}</h3>`;
      if (t.startsWith("# "))  return `<h2 class="ai-h2">${t.slice(2).replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")}</h2>`;
      const lines = t.split("\n");
      const isSep = l => l.includes("|") && l.includes("-") && /^[\s|:-]+$/.test(l);
      if (lines.length >= 2 && lines[0].includes("|") && isSep(lines[1])) {
        const splitRow = l => { let s = l.trim(); if (s.startsWith("|")) s = s.slice(1); if (s.endsWith("|")) s = s.slice(0, -1); return s.split("|").map(c => c.trim()); };
        const bold = c => c.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
        const header = splitRow(lines[0]);
        const body = lines.slice(2).filter(l => l.includes("|")).map(splitRow);
        return `<table class="ai-table"><thead><tr>${header.map(h => `<th>${bold(h)}</th>`).join("")}</tr></thead><tbody>${body.map(row => `<tr>${row.map(c => `<td>${bold(c)}</td>`).join("")}</tr>`).join("")}</tbody></table>`;
      }
      if (lines.every(l => /^\d+\.\s/.test(l.trim()))) {
        return `<ol class="ai-ol">${lines.map(l => `<li>${l.replace(/^\d+\.\s/, "").replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")}</li>`).join("")}</ol>`;
      }
      if (lines.every(l => /^[-*]\s/.test(l.trim()))) {
        return `<ul class="ai-ul">${lines.map(l => `<li>${l.replace(/^[-*]\s/, "").replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")}</li>`).join("")}</ul>`;
      }
      return `<p class="ai-p">${t.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>").replace(/\n/g, "<br>")}</p>`;
    })
    .join("");

  const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>P&L Report — ${wsName}</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Source+Serif+Pro:wght@400;600;700&display=swap" rel="stylesheet">
<style>
  * { box-sizing: border-box; }
  @page { size: A4; margin: 16mm 14mm; }
  body { font-family: "Inter", -apple-system, "Segoe UI", sans-serif; color: #1a2733; line-height: 1.5; margin: 0; padding: 0; background: #f8fafc; font-size: 11px; }
  .num { font-family: "Inter", -apple-system, "Segoe UI", sans-serif; font-feature-settings: "tnum"; }
  .toolbar { position: sticky; top: 0; background: #0f172a; color: white; padding: 12px 24px; display: flex; justify-content: space-between; align-items: center; z-index: 1000; }
  .toolbar-title { font-size: 13px; font-weight: 600; letter-spacing: -.2px; }
  .toolbar-title small { color: #94a3b8; font-weight: 400; margin-left: 8px; }
  .toolbar button { background: white; color: #0f172a; border: none; padding: 9px 22px; border-radius: 6px; font-weight: 700; cursor: pointer; font-size: 12.5px; font-family: inherit; }
  .toolbar button:hover { background: #f1f5f9; }
  @media print { .toolbar { display: none !important; } body { background: white; } .page { box-shadow: none !important; margin: 0 !important; } }
  .page { max-width: 780px; margin: 24px auto; background: white; padding: 48px 56px 40px; box-shadow: 0 1px 3px rgba(0,0,0,.06), 0 8px 24px rgba(15,23,42,.08); min-height: 1000px; }
  .cover { border-bottom: 3px double #0f172a; padding-bottom: 22px; margin-bottom: 28px; }
  .brand-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 22px; }
  .brand { display: flex; align-items: center; gap: 10px; }
  .brand-mark { width: 28px; height: 28px; background: #0f172a; border-radius: 5px; display: flex; align-items: center; justify-content: center; color: white; font-weight: 800; font-size: 14px; letter-spacing: -.5px; }
  .brand-name { font-size: 14px; font-weight: 700; color: #0f172a; letter-spacing: -.3px; }
  .brand-name small { font-weight: 500; color: #64748b; margin-left: 6px; }
  .doc-ref { font-size: 10px; color: #94a3b8; font-family: "Inter", -apple-system, "Segoe UI", sans-serif; letter-spacing: .5px; }
  .cover-title { font-family: "Source Serif Pro", Georgia, serif; font-size: 32px; font-weight: 700; line-height: 1.1; color: #0f172a; letter-spacing: -1px; margin: 0 0 6px; }
  .cover-meta { display: flex; gap: 24px; margin-top: 14px; font-size: 11.5px; }
  .cover-meta div { border-left: 2px solid #e5e7eb; padding-left: 10px; }
  .cover-meta-label { color: #64748b; font-size: 9px; text-transform: uppercase; letter-spacing: 1px; font-weight: 700; margin-bottom: 3px; }
  .cover-meta-value { color: #0f172a; font-weight: 600; font-size: 12.5px; }
  .exec-summary { background: #f8fafc; border-left: 4px solid ${verdictColor}; padding: 18px 22px; margin-bottom: 28px; }
  .exec-summary-label { font-size: 9px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 1.2px; margin-bottom: 8px; }
  .exec-summary-verdict { font-family: "Source Serif Pro", serif; font-size: 22px; font-weight: 700; color: ${verdictColor}; letter-spacing: -.6px; margin-bottom: 6px; }
  .exec-summary-text { font-size: 12px; color: #475569; line-height: 1.55; }
  .exec-summary-text strong { color: #0f172a; }
  .section-h2 { font-family: "Source Serif Pro", serif; font-size: 16px; font-weight: 700; color: #0f172a; letter-spacing: -.4px; margin: 32px 0 14px; padding-bottom: 8px; border-bottom: 1px solid #cbd5e1; display: flex; align-items: baseline; justify-content: space-between; }
  .section-h2 small { font-family: "Inter", sans-serif; font-size: 10px; font-weight: 500; color: #64748b; letter-spacing: 0; }
  .kpi-grid { display: grid; grid-template-columns: repeat(4, 1fr); border: 1px solid #e2e8f0; border-radius: 4px; overflow: hidden; }
  .kpi { padding: 14px 16px; border-right: 1px solid #e2e8f0; background: white; }
  .kpi:last-child { border-right: none; }
  .kpi-label { font-size: 8.5px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 6px; }
  .kpi-value { font-size: 18px; font-weight: 700; color: #0f172a; letter-spacing: -.5px; line-height: 1.1; margin-bottom: 2px; }
  .kpi-value.green { color: #0f7048; }
  .kpi-value.red { color: #a52727; }
  .kpi-sub { font-size: 9.5px; color: #94a3b8; font-weight: 500; }
  .pl-table { width: 100%; border-collapse: collapse; font-size: 10.5px; border: 1px solid #e2e8f0; border-radius: 4px; overflow: hidden; }
  .pl-table th { background: #f8fafc; padding: 9px 12px; text-align: left; font-size: 9px; text-transform: uppercase; font-weight: 700; color: #475569; letter-spacing: 1px; border-bottom: 1px solid #e2e8f0; }
  .pl-table th.right { text-align: right; }
  .pl-table td { padding: 8px 12px; border-bottom: 1px solid #f1f5f9; color: #1a2733; }
  .pl-table td.label { font-weight: 500; }
  .pl-table td.right { text-align: right; }
  .pl-table td.num { font-family: "Inter", -apple-system, "Segoe UI", sans-serif; font-weight: 500; }
  .pl-table td.note { color: #94a3b8; font-size: 9.5px; font-weight: 500; }
  .pl-table tr.subtotal td { background: #fafbfc; font-weight: 700; }
  .pl-table tr.deduction td.num { color: #a52727; }
  .pl-table tr.total td { background: #ecfdf5; font-weight: 700; border-top: 2px solid #0f7048; border-bottom: none; }
  .pl-table tr.total td.num { color: #0f7048; }
  .pl-table tr.total.loss td { background: #fef2f2; border-top-color: #a52727; }
  .pl-table tr.total.loss td.num { color: #a52727; }
  .platform-row { display: flex; justify-content: space-between; align-items: center; padding: 11px 14px; border: 1px solid #e2e8f0; border-radius: 4px; margin-bottom: 6px; font-size: 11px; background: white; }
  .platform-name { display: flex; align-items: center; gap: 9px; font-weight: 600; color: #0f172a; }
  .platform-name .dot { width: 9px; height: 9px; border-radius: 5px; }
  .platform-stats { font-family: "Inter", -apple-system, "Segoe UI", sans-serif; color: #64748b; font-weight: 500; font-size: 10.5px; }
  .platform-stats .profit { font-weight: 700; }
  .platform-stats .green { color: #0f7048; }
  .platform-stats .red { color: #a52727; }
  .ai-section { background: #f8fafc; border: 1px solid #e2e8f0; border-left: 4px solid #0f172a; padding: 24px 28px; margin-top: 4px; }
  .ai-section .ai-h3 { font-family: "Source Serif Pro", serif; font-size: 14px; font-weight: 700; color: #0f172a; margin: 18px 0 8px; letter-spacing: -.3px; }
  .ai-section .ai-h3:first-child { margin-top: 0; }
  .ai-section .ai-h2 { font-family: "Source Serif Pro", serif; font-size: 16px; font-weight: 700; color: #0f172a; margin: 20px 0 10px; }
  .ai-section .ai-p { margin: 0 0 10px; line-height: 1.65; font-size: 11.5px; color: #334155; }
  .ai-section .ai-p strong { color: #0f172a; font-weight: 600; }
  .ai-section .ai-ol, .ai-section .ai-ul { padding-left: 20px; margin: 8px 0 12px; }
  .ai-section .ai-ol li, .ai-section .ai-ul li { margin-bottom: 6px; line-height: 1.65; font-size: 11.5px; color: #334155; }
  .ai-section .ai-ol li strong, .ai-section .ai-ul li strong { color: #0f172a; font-weight: 600; }
  .ai-section .ai-table { width: 100%; border-collapse: collapse; margin: 10px 0 14px; font-size: 9.5px; }
  .ai-section .ai-table th { background: #eef2f7; text-align: left; padding: 6px 8px; font-weight: 700; color: #0f172a; border: 1px solid #d8dee9; }
  .ai-section .ai-table td { padding: 6px 8px; border: 1px solid #eef2f7; color: #334155; vertical-align: top; }
  .footer { margin-top: 36px; padding-top: 16px; border-top: 1px solid #e2e8f0; display: flex; justify-content: space-between; font-size: 9px; color: #94a3b8; font-weight: 500; letter-spacing: .3px; }
  .footer strong { color: #475569; font-weight: 600; }
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
</style>
</head>
<body>
<div class="toolbar">
  <span class="toolbar-title">P&amp;L Report <small>${wsName} · ${period}</small></span>
  <button onclick="window.print()">Save as PDF →</button>
</div>
<div class="page">
  <div class="cover">
    <div class="brand-row">
      <div class="brand">
        <div class="brand-mark">N</div>
        <div class="brand-name">Netrev.ai <small>P&amp;L Intelligence</small></div>
      </div>
      <div class="doc-ref">REF: ${reportRef}</div>
    </div>
    <h1 class="cover-title">Profit &amp; Loss<br>Period Report</h1>
    <div class="cover-meta">
      <div><div class="cover-meta-label">Location</div><div class="cover-meta-value">${wsName}</div></div>
      <div><div class="cover-meta-label">Period</div><div class="cover-meta-value">${period}</div></div>
      <div><div class="cover-meta-label">Generated</div><div class="cover-meta-value">${today}</div></div>
      <div><div class="cover-meta-label">Currency</div><div class="cover-meta-value">${currency}</div></div>
    </div>
  </div>
  <div class="exec-summary">
    <div class="exec-summary-label">Executive summary</div>
    <div class="exec-summary-verdict">${verdict}</div>
    <div class="exec-summary-text">
      Generated <strong>${fmtCurrency(totals.gross)}</strong> in gross sales with a true profit of
      <strong>${fmtCurrency(totals.profit)}</strong> (${(profitPctOfGross*100).toFixed(1)}% margin).
      Contribution margin: <strong>${(contribPct*100).toFixed(1)}%</strong>.
      Receivable from aggregators: <strong>${(receivablePctOfGross*100).toFixed(1)}%</strong> of gross.
    </div>
  </div>
  <h2 class="section-h2">Key metrics <small>${period}</small></h2>
  <div class="kpi-grid">
    <div class="kpi"><div class="kpi-label">Gross Sales</div><div class="kpi-value num">${fmtCurrency(totals.gross)}</div><div class="kpi-sub">100% baseline</div></div>
    <div class="kpi"><div class="kpi-label">Net Receivable</div><div class="kpi-value num">${fmtCurrency(totals.netReceivable)}</div><div class="kpi-sub">${(receivablePctOfGross*100).toFixed(1)}% of gross</div></div>
    <div class="kpi"><div class="kpi-label">True Profit</div><div class="kpi-value num ${totals.profit >= 0 ? "green" : "red"}">${fmtCurrency(totals.profit)}</div><div class="kpi-sub">${totals.profit >= 0 ? "after all costs" : "loss-making"}</div></div>
    <div class="kpi"><div class="kpi-label">Profit Margin</div><div class="kpi-value num ${profitPctOfGross >= 0 ? "green" : "red"}">${(profitPctOfGross*100).toFixed(1)}%</div><div class="kpi-sub">vs. gross sales</div></div>
  </div>
  <h2 class="section-h2">P&amp;L breakdown <small>line-by-line</small></h2>
  <table class="pl-table">
    <thead><tr><th>Line item</th><th class="right">Amount</th><th class="right">Note</th></tr></thead>
    <tbody>
      <tr class="subtotal"><td class="label">Gross sales</td><td class="right num">${fmtCurrency(totals.gross)}</td><td class="right note">100%</td></tr>
      ${(totals.gross - totals.net) >= 0
        ? `<tr class="deduction"><td class="label">− Discounts given</td><td class="right num">${fmtCurrency(totals.gross - totals.net)}</td><td class="right note">${(avgDiscountPct*100).toFixed(1)}% of gross</td></tr>`
        : `<tr class="deduction" style="background:#fef3f2"><td class="label" style="color:#a52727">⚠ Net exceeds gross by</td><td class="right num" style="color:#a52727">${fmtCurrency(totals.net - totals.gross)}</td><td class="right note" style="color:#a52727">data error — should be negative</td></tr>`}
      <tr class="subtotal"><td class="label">= Net sales</td><td class="right num">${fmtCurrency(totals.net)}</td><td class="right note">post-discount</td></tr>
      ${(totals.coFund || 0) > 0 ? `<tr><td class="label">+ Platform contribution</td><td class="right num" style="color:#0f7048">${fmtCurrency(totals.coFund)}</td><td class="right note">co-funded discounts</td></tr>
      <tr class="subtotal"><td class="label">= True net ticket</td><td class="right num">${fmtCurrency(totals.net + totals.coFund)}</td><td class="right note">commission &amp; fees charged on this</td></tr>` : ""}
      <tr class="deduction"><td class="label">− Aggregator commission</td><td class="right num">${fmtCurrency(totals.commission)}</td><td class="right note">${totals.gross > 0 ? (totals.commission/totals.gross*100).toFixed(1) : 0}% of gross</td></tr>
      <tr class="deduction"><td class="label">− Card processing fees</td><td class="right num">${fmtCurrency(totals.cardFees)}</td><td class="right note">${(totals.net + (totals.coFund || 0)) > 0 ? (totals.cardFees/(totals.net + (totals.coFund || 0))*100).toFixed(2) : 0}% of ${(totals.coFund || 0) > 0 ? "true net ticket" : "net"}</td></tr>
      <tr class="deduction"><td class="label">− CPC advertising</td><td class="right num">${fmtCurrency(totals.cpc)}</td><td class="right note">${(cpcPctOfNet*100).toFixed(1)}% of ${(totals.coFund || 0) > 0 ? "true net ticket" : "net"}</td></tr>
      <tr class="deduction"><td class="label">− Campaign fees</td><td class="right num">${fmtCurrency(totals.campaignFee)}</td><td class="right note">platform promos</td></tr>
      <tr class="subtotal"><td class="label">= Net receivable</td><td class="right num">${fmtCurrency(totals.netReceivable)}</td><td class="right note">${(receivablePctOfGross*100).toFixed(1)}% of gross</td></tr>
      <tr class="deduction"><td class="label">− VAT</td><td class="right num">${fmtCurrency(totals.vat)}</td><td class="right note">${((vatRate ?? 0)*100).toFixed(0)}% on net receivable</td></tr>
      <tr><td class="label">= Earnings after VAT</td><td class="right num">${fmtCurrency(totals.eav)}</td><td class="right note"></td></tr>
      <tr class="deduction"><td class="label">− COGS (cost of food)</td><td class="right num">${fmtCurrency(totals.cogs)}</td><td class="right note">${((cogsPct ?? 0)*100).toFixed(0)}% of gross</td></tr>
      <tr class="total ${totals.profit < 0 ? "loss" : ""}"><td class="label">= TRUE PROFIT</td><td class="right num">${fmtCurrency(totals.profit)}</td><td class="right note">${(profitPctOfGross*100).toFixed(1)}% margin</td></tr>
    </tbody>
  </table>
  ${perPlatform.length > 0 ? `
  <h2 class="section-h2">Platform performance <small>ranked by profit</small></h2>
  <div class="platform-list">
    ${perPlatform.map(p => `<div class="platform-row"><span class="platform-name"><span class="dot" style="background:${p.color}"></span>${p.name}</span><span class="platform-stats">Gross ${fmtCurrency(p.gross)} &nbsp;·&nbsp; Profit <span class="profit ${p.profit >= 0 ? "green" : "red"}">${fmtCurrency(p.profit)}</span> (${(p.profitPct*100).toFixed(1)}% margin)</span></div>`).join("")}
  </div>` : ""}
  ${aiText ? `<h2 class="section-h2">Analysis &amp; recommendations <small>data-driven insights</small></h2><div class="ai-section">${aiHtml}</div>` : ""}
  <div class="disclaimer" style="margin-top:20px;padding:12px 14px;background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;font-size:9px;line-height:1.6;color:#64748b;">
    <strong style="color:#475569;">Disclaimer:</strong> Netrev.ai provides estimates for planning and informational purposes only. The figures shown are calculated from the data you entered and your configured rates — they are not guaranteed to be accurate and may not reflect your actual platform statements. This is not financial, accounting, tax, or legal advice. Always verify figures against your official aggregator statements and consult a qualified professional before making business decisions.
  </div>
  <div class="footer">
    <span>Netrev.ai · netrev.ai · <strong>Confidential</strong></span>
    <span>Generated ${today} · REF ${reportRef}</span>
  </div>
</div>
</body>
</html>`;

  printWin.document.write(html);
  printWin.document.close();
}

// Styled PDF for the SECOND report (menu pricing). Same Netrev/A4 look as the
// period PDF, but it just renders the pricing analysis markdown (suggested
// prices, discount tiers, best platform per item) under a cover + disclaimer.
function generatePricingPDF({ wsName, scopeLabel, currency, sym, aiText }) {
  const printWin = window.open("", "_blank", "width=900,height=1100");
  if (!printWin) {
    alert("Please allow popups for this site to download the PDF.");
    return;
  }
  const today = new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });
  const reportRef = `MP-${Date.now().toString(36).toUpperCase().slice(-6)}`;

  const aiHtml = (aiText || "")
    .split(/\n\s*\n/)
    .map(block => {
      const t = block.trim();
      if (!t) return "";
      if (t.startsWith("## ")) return `<h3 class="ai-h3">${t.slice(3).replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")}</h3>`;
      if (t.startsWith("# "))  return `<h2 class="ai-h2">${t.slice(2).replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")}</h2>`;
      const lines = t.split("\n");
      const isSep = l => l.includes("|") && l.includes("-") && /^[\s|:-]+$/.test(l);
      if (lines.length >= 2 && lines[0].includes("|") && isSep(lines[1])) {
        const splitRow = l => { let s = l.trim(); if (s.startsWith("|")) s = s.slice(1); if (s.endsWith("|")) s = s.slice(0, -1); return s.split("|").map(c => c.trim()); };
        const bold = c => c.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
        const header = splitRow(lines[0]);
        const body = lines.slice(2).filter(l => l.includes("|")).map(splitRow);
        return `<table class="ai-table"><thead><tr>${header.map(h => `<th>${bold(h)}</th>`).join("")}</tr></thead><tbody>${body.map(row => `<tr>${row.map(c => `<td>${bold(c)}</td>`).join("")}</tr>`).join("")}</tbody></table>`;
      }
      if (lines.every(l => /^\d+\.\s/.test(l.trim()))) {
        return `<ol class="ai-ol">${lines.map(l => `<li>${l.replace(/^\d+\.\s/, "").replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")}</li>`).join("")}</ol>`;
      }
      if (lines.every(l => /^[-*]\s/.test(l.trim()))) {
        return `<ul class="ai-ul">${lines.map(l => `<li>${l.replace(/^[-*]\s/, "").replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")}</li>`).join("")}</ul>`;
      }
      return `<p class="ai-p">${t.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>").replace(/\n/g, "<br>")}</p>`;
    })
    .join("");

  const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>Menu Pricing Report — ${wsName}</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Source+Serif+Pro:wght@400;600;700&display=swap" rel="stylesheet">
<style>
  * { box-sizing: border-box; }
  @page { size: A4; margin: 16mm 14mm; }
  body { font-family: "Inter", -apple-system, "Segoe UI", sans-serif; color: #1a2733; line-height: 1.5; margin: 0; padding: 0; background: #f8fafc; font-size: 11px; }
  .toolbar { position: sticky; top: 0; background: #0f172a; color: white; padding: 12px 24px; display: flex; justify-content: space-between; align-items: center; z-index: 1000; }
  .toolbar-title { font-size: 13px; font-weight: 600; letter-spacing: -.2px; }
  .toolbar-title small { color: #94a3b8; font-weight: 400; margin-left: 8px; }
  .toolbar button { background: white; color: #0f172a; border: none; padding: 9px 22px; border-radius: 6px; font-weight: 700; cursor: pointer; font-size: 12.5px; font-family: inherit; }
  @media print { .toolbar { display: none !important; } body { background: white; } .page { box-shadow: none !important; margin: 0 !important; } }
  .page { max-width: 780px; margin: 24px auto; background: white; padding: 48px 56px 40px; box-shadow: 0 1px 3px rgba(0,0,0,.06), 0 8px 24px rgba(15,23,42,.08); min-height: 1000px; }
  .cover { border-bottom: 3px double #0f172a; padding-bottom: 22px; margin-bottom: 28px; }
  .brand-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 22px; }
  .brand { display: flex; align-items: center; gap: 10px; }
  .brand-mark { width: 28px; height: 28px; background: #0f172a; border-radius: 5px; display: flex; align-items: center; justify-content: center; color: white; font-weight: 800; font-size: 14px; letter-spacing: -.5px; }
  .brand-name { font-size: 14px; font-weight: 700; color: #0f172a; letter-spacing: -.3px; }
  .brand-name small { font-weight: 500; color: #64748b; margin-left: 6px; }
  .doc-ref { font-size: 10px; color: #94a3b8; font-family: "Inter", -apple-system, "Segoe UI", sans-serif; letter-spacing: .5px; }
  .cover-title { font-family: "Source Serif Pro", Georgia, serif; font-size: 32px; font-weight: 700; line-height: 1.1; color: #0f172a; letter-spacing: -1px; margin: 0 0 6px; }
  .cover-meta { display: flex; gap: 24px; margin-top: 14px; font-size: 11.5px; }
  .cover-meta div { border-left: 2px solid #e5e7eb; padding-left: 10px; }
  .cover-meta-label { color: #64748b; font-size: 9px; text-transform: uppercase; letter-spacing: 1px; font-weight: 700; margin-bottom: 3px; }
  .cover-meta-value { color: #0f172a; font-weight: 600; font-size: 12.5px; }
  .ai-section { background: #f8fafc; border: 1px solid #e2e8f0; border-left: 4px solid #0f172a; padding: 24px 28px; margin-top: 4px; }
  .ai-section .ai-h3 { font-family: "Source Serif Pro", serif; font-size: 14px; font-weight: 700; color: #0f172a; margin: 18px 0 8px; letter-spacing: -.3px; }
  .ai-section .ai-h3:first-child { margin-top: 0; }
  .ai-section .ai-h2 { font-family: "Source Serif Pro", serif; font-size: 16px; font-weight: 700; color: #0f172a; margin: 20px 0 10px; }
  .ai-section .ai-p { margin: 0 0 10px; line-height: 1.65; font-size: 11.5px; color: #334155; }
  .ai-section .ai-p strong { color: #0f172a; font-weight: 600; }
  .ai-section .ai-ol, .ai-section .ai-ul { padding-left: 20px; margin: 8px 0 12px; }
  .ai-section .ai-ol li, .ai-section .ai-ul li { margin-bottom: 6px; line-height: 1.65; font-size: 11.5px; color: #334155; }
  .ai-section .ai-ol li strong, .ai-section .ai-ul li strong { color: #0f172a; font-weight: 600; }
  .ai-section .ai-table { width: 100%; border-collapse: collapse; margin: 10px 0 14px; font-size: 9.5px; }
  .ai-section .ai-table th { background: #eef2f7; text-align: left; padding: 6px 8px; font-weight: 700; color: #0f172a; border: 1px solid #d8dee9; }
  .ai-section .ai-table td { padding: 6px 8px; border: 1px solid #eef2f7; color: #334155; vertical-align: top; }
  .footer { margin-top: 36px; padding-top: 16px; border-top: 1px solid #e2e8f0; display: flex; justify-content: space-between; font-size: 9px; color: #94a3b8; font-weight: 500; letter-spacing: .3px; }
  .footer strong { color: #475569; font-weight: 600; }
  .disclaimer { margin-top: 20px; padding: 12px 14px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; font-size: 9px; line-height: 1.6; color: #64748b; }
  .disclaimer strong { color: #475569; }
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
</style>
</head>
<body>
<div class="toolbar">
  <span class="toolbar-title">Menu Pricing Report <small>${wsName} · ${scopeLabel}</small></span>
  <button onclick="window.print()">Save as PDF →</button>
</div>
<div class="page">
  <div class="cover">
    <div class="brand-row">
      <div class="brand">
        <div class="brand-mark">N</div>
        <div class="brand-name">Netrev.ai <small>Menu Pricing</small></div>
      </div>
      <div class="doc-ref">REF: ${reportRef}</div>
    </div>
    <h1 class="cover-title">Menu Pricing<br>Report</h1>
    <div class="cover-meta">
      <div><div class="cover-meta-label">Location</div><div class="cover-meta-value">${wsName}</div></div>
      <div><div class="cover-meta-label">Scope</div><div class="cover-meta-value">${scopeLabel}</div></div>
      <div><div class="cover-meta-label">Generated</div><div class="cover-meta-value">${today}</div></div>
      <div><div class="cover-meta-label">Currency</div><div class="cover-meta-value">${currency}</div></div>
    </div>
  </div>
  ${aiText ? `<div class="ai-section">${aiHtml}</div>` : `<p>No pricing analysis generated yet.</p>`}
  <div class="disclaimer">
    <strong>Disclaimer:</strong> Suggested selling prices are estimates calculated from the cost, commission, card-fee, VAT and discount figures you entered, plus a per-order ${sym}${currency === "AED" ? "5" : currency === "INR" ? "100" : "1"} advertising allowance. They target a 10–15% net margin but are not guaranteed and do not account for demand, competitor pricing, or platform-specific rules. Always validate against your own margins before changing menu prices.
  </div>
  <div class="footer">
    <span>Netrev.ai · netrev.ai · <strong>Confidential</strong></span>
    <span>Generated ${today} · REF ${reportRef}</span>
  </div>
</div>
</body>
</html>`;

  printWin.document.write(html);
  printWin.document.close();
}

export function AdminAnalysis({ws, cfg, currency="GBP", chId, range="ALL", customFrom:pCustomFrom="", customTo:pCustomTo="", onNavigate, paidAccess=true, onShowUpgrade}) {
  // i18n: alias t→tr because `t` is already used below for the totals object.
  const { t: tr } = useT();
  const sym = currency==="AED" ? "AED " : currency==="USD" ? "$" : currency==="INR" ? "₹" : "£";
  // Insights shares the pill-bar date filter (range/customFrom/customTo from
  // AdminView). Map the shared range model onto this view's period logic.
  const customFrom = pCustomFrom;
  const customTo = pCustomTo;
  const periodMode = range==="CUSTOM" ? "custom"
    : (range==="ALL"||range==="all") ? "all"
    : range==="1D" ? "today"
    : range==="1DY" ? "yesterday"
    : range==="1W" ? "week"
    : "range"; // 1M/3M/1Y etc → use the shared rangeWindow below
  const [aiInsight, setAiInsight] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState("");
  const [aiCachedAt, setAiCachedAt] = useState(null); // ISO timestamp when current insight was generated/loaded
  // Second report — menu pricing — produced by the SAME "Generate analysis" button.
  const [aiPricing, setAiPricing] = useState("");
  const [aiPricingError, setAiPricingError] = useState("");
  const [aiPricingCachedAt, setAiPricingCachedAt] = useState(null);

  // Clear any displayed AI insight when the data being analyzed changes.
  // User must click Generate again — we'll then check cache or call AI.
  useEffect(() => {
    setAiInsight("");
    setAiError("");
    setAiCachedAt(null);
    setAiPricing("");
    setAiPricingError("");
    setAiPricingCachedAt(null);
  }, [periodMode, ws?.id, chId, customFrom, customTo]);

  // Aggregate all platforms (or filter to selected channel)
  const channels = chId && ws?.channels?.[chId] ? [ws.channels[chId]] : Object.values(ws?.channels || {});

  // Determine date range — cutoff/rangeEnd come from the SHARED rangeWindow
  // helper (one formula for row-selection AND the CPC window), so Insights ties
  // out with Overview / Daily Log across every preset (incl. Yesterday + 7D).
  const today = new Date(); today.setHours(23,59,59,999);
  const fmtShort = d => d.toLocaleDateString("en-GB", {day: "2-digit", month: "short", year: "numeric"});
  let cutoff, rangeEnd, rangeLabel;
  if (periodMode === "all") {
    // Find first log entry date so the label reflects the actual span.
    let firstEntry = null;
    channels.forEach(c => (c.history || []).forEach(r => {
      if (r.dateObj instanceof Date && (!firstEntry || r.dateObj < firstEntry)) firstEntry = r.dateObj;
    }));
    cutoff = firstEntry ? new Date(firstEntry.getTime() - 1000) : new Date(0);
    rangeEnd = today;
    rangeLabel = firstEntry ? `${fmtShort(firstEntry)} → ${fmtShort(today)}` : "All entries";
  } else {
    // custom / today / yesterday / week / range → the shared window.
    const w = rangeWindow(range, customFrom, customTo);
    cutoff = w.cutoff;
    rangeEnd = w.rangeEnd;
    // Single-day presets show one date; ranges show "from → to".
    rangeLabel = (periodMode === "today" || periodMode === "yesterday")
      ? fmtShort(cutoff)
      : `${fmtShort(cutoff)} → ${fmtShort(rangeEnd)}`;
  }

  const allRows = channels.flatMap(c =>
    (c.history || [])
      .filter(r => {
        // For "all" period, include every entry regardless of date validity
        if (periodMode === "all") return true;
        // Otherwise apply normal date range filter
        return r.dateObj instanceof Date && !isNaN(r.dateObj.getTime()) && r.dateObj >= cutoff && r.dateObj <= rangeEnd;
      })
      .map(r => ({
        ...r,
        coFund: coFundAmount(r),
        ...calcDaily(r.gross, effNet(r), r.cpc, {
          aggCommission: r.commission ?? c.commission,
          cardFee: (r.cardFeePct ?? c.cardFeePct ?? 0),
          vatRate: ws?.vatRate ?? 0,
          cogsPct: ws?.cogsPct ?? 0,
          vatOnFees: ws?.vatOnFees === true,
        }),
        platform: c.platform,
        channelColor: c.color,
      }))
  );

  // Aggregate metrics
  const t = allRows.reduce((a, r) => ({
    gross: a.gross + (r.gross || 0),
    net:   a.net   + (r.net || r.gross || 0),
    coFund: a.coFund + (r.coFund || 0),
    commission: a.commission + (r.commissionAmt || 0),
    cardFees: a.cardFees + (r.cardFees || 0),
    cpc: a.cpc + (r.cpc || 0),
    campaignFee: a.campaignFee + (r.campaignFee || 0),
    netReceivable: a.netReceivable + (r.netReceivable || 0),
    vat: a.vat + (r.vat || 0),
    vatOnComm: a.vatOnComm + (r.vatOnComm || 0),
    vatOnCard: a.vatOnCard + (r.vatOnCard || 0),
    vatOnCpc: a.vatOnCpc + (r.vatOnCpc || 0),
    eav: a.eav + (r.earningAfterVat || 0),
    cogs: a.cogs + (r.cogs || 0),
    profit: a.profit + (r.trueProfit || 0),
  }), {gross:0,net:0,coFund:0,commission:0,cardFees:0,cpc:0,campaignFee:0,netReceivable:0,vat:0,vatOnComm:0,vatOnCard:0,vatOnCpc:0,eav:0,cogs:0,profit:0});

  // ── CPC / campaign spend (period-level, from the allocations) ────────
  // CPC is entered as date-range allocations and spread per day. Per-row
  // calcDaily above already deducted the CPC for days that HAVE a sales entry
  // (that's t.cpc so far). Here we top it up to the FULL CPC allocated to the
  // period — including in-period days of a range that have NO sales entry — so
  // the full committed spend comes off net receivable + profit and the figure
  // matches the CPC allocations table. "all" period = the full entered amounts.
  // CPC window from the SHARED helper (winFrom = cutoff, winTo capped at today;
  // ALL → winFrom null = full entered amounts). One formula across all views.
  const { winFrom: cpcWinFrom, winTo: cpcWinTo } = rangeWindow(range, customFrom, customTo);
  const cpcScopeChannels = chId && ws?.channels?.[chId] ? [ws.channels[chId]] : Object.values(ws?.channels || {});
  const cpcScopeAllocs = cpcScopeChannels.flatMap(c => (c.cpcAllocations || []).map(a => ({ ...a, channelId: a.channelId ?? c.id })));
  // Subscription span = inclusive day-span of the rows (or 30 if none); scope =
  // the same channels as the CPC scope. Insight INCLUDES the subscription.
  const subPeriodDays = (() => {
    const dts = allRows.map(r => r.dateObj?.getTime()).filter(x => !isNaN(x));
    if (dts.length === 0) return 30;
    return Math.max(1, Math.round((Math.max(...dts) - Math.min(...dts)) / 86400000) + 1);
  })();
  // ── Period-level P&L adjustments — SINGLE source of truth (calc.js) ───
  // CPC full-period top-up + subscription + recoverable fee-VAT + output VAT +
  // earnings-after-VAT + true profit, all in one place. calcDaily untouched.
  applyPeriodAdjustments(t, {
    allocs: cpcScopeAllocs, winFrom: cpcWinFrom, winTo: cpcWinTo,
    vatRate: ws?.vatRate ?? 0,
    includeSubscription: true, subChannels: cpcScopeChannels, subPeriodDays,
  });

  const totalDiscount = t.gross - t.net;                              // TOTAL customer discount (unchanged)
  const avgDiscountPct = t.gross > 0 ? totalDiscount / t.gross : 0;  // total — the ladder's "% of gross" note
  // Platform co-funding (lib/coFund.js): the platform-paid share of discounts,
  // added back to the net ticket PER ROW. netEff = the net commission/card fees
  // are actually charged on — the denominator for "% of net" ratios, so the
  // "Card fees above standard" / CPC flags don't fire falsely.
  const coFundTotal = +(t.coFund || 0).toFixed(2);
  const netEff = t.net + coFundTotal;
  const ownDiscountPct = t.gross > 0 ? Math.max(0, totalDiscount - coFundTotal) / t.gross : 0; // restaurant-funded
  const receivablePctOfGross = t.gross > 0 ? t.netReceivable / t.gross : 0;
  const profitPctOfGross = t.gross > 0 ? t.profit / t.gross : 0;
  const cpcPctOfNet = netEff > 0 ? t.cpc / netEff : 0;
  const cardFeePctOfNet = netEff > 0 ? t.cardFees / netEff : 0;
  const contribPct = t.gross > 0 ? (t.eav - t.cogs) / t.gross : 0;

  // Per-platform breakdown — ALWAYS uses ALL channels in the workspace, regardless of chId filter.
  // This way users see comparison context even when filtered to a single platform.
  const perPlatform = Object.values(ws?.channels || {}).map(c => {
    const rows = (c.history || [])
      .filter(r => {
        if (periodMode === "all") return true;
        return r.dateObj instanceof Date && !isNaN(r.dateObj.getTime()) && r.dateObj >= cutoff && r.dateObj <= rangeEnd;
      })
      .map(r => ({
        ...r,
        coFund: coFundAmount(r),
        ...calcDaily(r.gross, effNet(r), r.cpc, {
          aggCommission: r.commission ?? c.commission,
          cardFee: (r.cardFeePct ?? c.cardFeePct ?? 0),
          vatRate: ws?.vatRate ?? 0,
          cogsPct: ws?.cogsPct ?? 0,
          vatOnFees: ws?.vatOnFees === true,
        }),
      }));
    const tt = rows.reduce((a, r) => ({
      gross: a.gross + (r.gross || 0),
      net: a.net + (r.net || r.gross || 0),
      coFund: a.coFund + (r.coFund || 0),
      commission: a.commission + (r.commissionAmt || 0),
      cardFees: a.cardFees + (r.cardFees || 0),
      cpc: a.cpc + (r.cpc || 0),
      vatOnComm: a.vatOnComm + (r.vatOnComm || 0),
      vatOnCard: a.vatOnCard + (r.vatOnCard || 0),
      vatOnCpc: a.vatOnCpc + (r.vatOnCpc || 0),
      netReceivable: a.netReceivable + (r.netReceivable || 0),
      cogs: a.cogs + (r.cogs || 0),
    }), {gross:0,net:0,coFund:0,commission:0,cardFees:0,cpc:0,vatOnComm:0,vatOnCard:0,vatOnCpc:0,netReceivable:0,cogs:0});
    // Top up this platform's CPC to the FULL period allocation (incl. days with
    // no sales entry) via the SAME central function (no subscription per-platform).
    const pAllocs  = (c.cpcAllocations || []).map(a => ({ ...a, channelId: a.channelId ?? c.id }));
    applyPeriodAdjustments(tt, {
      allocs: pAllocs, winFrom: cpcWinFrom, winTo: cpcWinTo,
      vatRate: ws?.vatRate ?? 0, includeSubscription: false,
    });
    return {
      id: c.id,
      name: c.platform,
      color: c.color,
      gross: tt.gross,
      net: tt.net,
      coFund: +(tt.coFund || 0).toFixed(2),
      profit: tt.profit,
      commission: tt.commission,
      cardFees: tt.cardFees,
      cpc: tt.cpc,
      cogs: tt.cogs,
      profitPct: tt.gross > 0 ? tt.profit / tt.gross : 0,
      commissionPct: tt.gross > 0 ? tt.commission / tt.gross : 0,
      cpcPctOfNet: (tt.net + (tt.coFund || 0)) > 0 ? tt.cpc / (tt.net + (tt.coFund || 0)) : 0,
      contribPct: tt.gross > 0 ? (tt.eav - tt.cogs) / tt.gross : 0,
      entries: rows.length,
    };
  }).filter(p => p.gross > 0).sort((a,b) => b.profit - a.profit);

  // Sortable + filterable Per-platform performance table. Default = profit desc
  // (matches "Sorted by profit"). The BEST badge tracks the highest-profit
  // platform by id so it stays correct even after a user re-sorts the columns.
  const ppSortCols = useMemo(()=>([
    {key:"name",       label:tr("analysis.col.platform"),   type:"text",   value:p=>p.name, align:"left"},
    {key:"entries",    label:tr("analysis.col.entries"),    type:"number", value:p=>p.entries},
    {key:"gross",      label:tr("analysis.col.gross"),      type:"number", value:p=>p.gross},
    {key:"net",        label:tr("analysis.col.net"),        type:"number", value:p=>p.net},
    {key:"commission", label:tr("analysis.col.commission"), type:"number", value:p=>p.commission},
    {key:"cpc",        label:tr("analysis.col.cpc"),        type:"number", value:p=>p.cpc},
    {key:"cogs",       label:tr("analysis.col.cogs"),       type:"number", value:p=>p.cogs},
    {key:"profit",     label:tr("analysis.col.trueProfit"), type:"number", value:p=>p.profit},
    {key:"margin",     label:tr("analysis.col.margin"),     type:"number", value:p=>p.profitPct},
  ]),[tr]);
  const ppTable = useTableSortFilter(perPlatform, ppSortCols, {initialSortKey:"profit", initialSortDir:"desc"});
  const bestPlatformId = perPlatform.length > 1
    ? perPlatform.reduce((best,p)=> p.profit > best.profit ? p : best, perPlatform[0])?.id
    : null;

  // Pie chart data — where each £ of gross goes
  const pieSegments = [
    {name: coFundTotal > 0 ? tr("analysis.seg.discountsYouFunded") : tr("analysis.seg.discountsGiven"), value: Math.max(0, totalDiscount - coFundTotal), color: "#fd7e14"},
    {name: tr("analysis.seg.commission"), value: t.commission, color: "#e03131"},
    {name: tr("analysis.seg.cardFees"), value: t.cardFees, color: "#fcc419"},
    {name: tr("analysis.seg.cpcAdSpend"), value: t.cpc, color: "#fa8c16"},
    {name: tr("analysis.seg.vatOnFees"), value: t.totalFeeVat, color: "#9c36b5"},
    {name: tr("analysis.seg.subscriptionFee"), value: t.subscriptionFee, color: "#d6336c"},
    {name: tr("analysis.seg.cogsFoodCost"), value: t.cogs, color: "#7048e8"},
    {name: tr("analysis.seg.trueProfit"), value: Math.max(0, t.profit), color: "#0ca678"},
    ...(t.profit < 0 ? [{name: tr("analysis.seg.lossOver"), value: Math.abs(t.profit), color: "#c92a2a"}] : []),
  ].filter(s => s.value > 0.01);

  // Build a simple SVG pie chart
  const pieTotal = pieSegments.reduce((s, x) => s + x.value, 0);
  const pieRadius = 80;
  const pieCx = 100, pieCy = 100;
  let cumulative = 0;
  const piePaths = pieSegments.map(seg => {
    const startAngle = (cumulative / pieTotal) * 2 * Math.PI - Math.PI / 2;
    cumulative += seg.value;
    const endAngle = (cumulative / pieTotal) * 2 * Math.PI - Math.PI / 2;
    const x1 = pieCx + pieRadius * Math.cos(startAngle);
    const y1 = pieCy + pieRadius * Math.sin(startAngle);
    const x2 = pieCx + pieRadius * Math.cos(endAngle);
    const y2 = pieCy + pieRadius * Math.sin(endAngle);
    const largeArc = (seg.value / pieTotal) > 0.5 ? 1 : 0;
    return {
      d: `M ${pieCx} ${pieCy} L ${x1} ${y1} A ${pieRadius} ${pieRadius} 0 ${largeArc} 1 ${x2} ${y2} Z`,
      seg,
      pct: (seg.value / pieTotal) * 100,
    };
  });

  // Rule-based flags & highlights
  const flags = [];
  if (cpcPctOfNet > 0.08) flags.push({type:"warn", icon:"⚠️", title:tr("analysis.flags.cpcHigh.title"), text:`${tr("analysis.flags.cpcHigh.text.prefix")}${(cpcPctOfNet*100).toFixed(1)}%${tr(coFundTotal > 0 ? "analysis.flags.cpcHigh.text.suffixCoFund" : "analysis.flags.cpcHigh.text.suffix")}`});
  if (ownDiscountPct > 0.15) flags.push({type:"warn", icon:"🏷️", title:tr("analysis.flags.heavyDiscount.title"), text:`${tr(coFundTotal > 0 ? "analysis.flags.heavyDiscount.text.prefixOwn" : "analysis.flags.heavyDiscount.text.prefix")}${(ownDiscountPct*100).toFixed(1)}%${tr("analysis.flags.heavyDiscount.text.suffix")}`});
  if (contribPct < 0.08 && t.gross > 0) flags.push({type:"warn", icon:"📉", title:tr("analysis.flags.thinMargin.title"), text:`${tr("analysis.flags.thinMargin.text.prefix")}${(contribPct*100).toFixed(1)}%${tr("analysis.flags.thinMargin.text.suffix")}`});
  if (cardFeePctOfNet > 0.03) flags.push({type:"warn", icon:"💳", title:tr("analysis.flags.cardFeesHigh.title"), text:`${tr("analysis.flags.cardFeesHigh.text.prefix")}${(cardFeePctOfNet*100).toFixed(2)}%${tr(coFundTotal > 0 ? "analysis.flags.cardFeesHigh.text.suffixCoFund" : "analysis.flags.cardFeesHigh.text.suffix")}`});

  // Highlights
  const bestDay = allRows.length > 0 ? allRows.reduce((a, r) => r.trueProfit > a.trueProfit ? r : a, allRows[0]) : null;
  const worstDay = allRows.length > 0 ? allRows.reduce((a, r) => r.trueProfit < a.trueProfit ? r : a, allRows[0]) : null;
  const bestPlatform = perPlatform[0];
  const worstPlatform = perPlatform.length > 1 ? perPlatform[perPlatform.length - 1] : null;

  // Total monthly fixed costs (workspace-level)
  const workspaceFixedMonthly = (() => {
    const fc = ws?.fixedCosts || {rent:0, wages:0, utilities:0, other:0};
    return (fc.rent||0) + (fc.wages||0) + (fc.utilities||0) + (fc.other||0);
  })();

  // When filtered to a single channel, allocate fixed costs proportionally based on
  // that channel's share of total workspace gross over the same period. This gives
  // a fair per-platform P&L instead of dumping all fixed costs onto one channel.
  const totalFixedMonthly = (() => {
    if (!chId) return workspaceFixedMonthly; // viewing all channels = full fixed costs
    if (workspaceFixedMonthly === 0) return 0;

    // Compute total workspace gross for the same period (across ALL channels)
    const allChannelsRows = Object.values(ws?.channels || {}).flatMap(c =>
      (c.history || []).filter(r => {
        if (periodMode === "all") return true;
        return r.dateObj instanceof Date && !isNaN(r.dateObj.getTime()) && r.dateObj >= cutoff && r.dateObj <= rangeEnd;
      })
    );
    const totalWsGross = allChannelsRows.reduce((sum, r) => sum + (r.gross || 0), 0);
    if (totalWsGross === 0) return workspaceFixedMonthly; // no data, fall back to full

    const channelGross = allRows.reduce((sum, r) => sum + (r.gross || 0), 0);
    const share = channelGross / totalWsGross;
    return workspaceFixedMonthly * share;
  })();

  // ── Break-even coverage (period-allocated fixed costs) ────────────────
  // Drives the coverage-led verdict: a business that covers its fixed costs is
  // "Healthy" even on a thin delivery margin. coverage = period contribution ÷
  // period fixed costs (≥100% = fixed costs covered). Computed here at component
  // scope so the PDF executive-summary verdict (generateInsightsPDF) reads the
  // SAME way as the on-screen AI verdict, which derives the same figures below.
  // Fixed costs prorated over the SELECTED period using each month's ACTUAL days
  // (same basis as the subscription); average-month fallback only for "All".
  const periodFixed = totalFixedMonthly * ((cpcWinFrom && cpcWinTo) ? monthsForSpan(cpcWinFrom, cpcWinTo) : subPeriodDays / 30.4375);
  const trueProfitAfterFixed = +(t.profit - periodFixed).toFixed(2);
  const breakEvenDailyGross = subPeriodDays > 0 && contribPct > 0
    ? (periodFixed / subPeriodDays) / contribPct  // daily fixed on the SAME calendar-month basis as periodFixed (not the 30.4375 average) → coverage = contribution ÷ periodFixed
    : 0;
  const avgDailyGross = subPeriodDays > 0 ? t.gross / subPeriodDays : 0;
  const coverageRatio = breakEvenDailyGross > 0 ? avgDailyGross / breakEvenDailyGross : 0;
  const hasFixedCosts = totalFixedMonthly > 0;

  // AI Insights — checks Supabase cache first, calls Claude if no fresh hit.
  // Cache invalidates automatically when data changes or after 7 days.
  async function runPeriodAnalysis(forceFresh = false) {
    setAiError("");
    setAiInsight("");
    setAiCachedAt(null);
    try {
      // 1) Compute fingerprint of current data (rows + rates + channel rates + period)
      const fpRows = allRows.map(r => ({
        date: r.date,
        channelId: r.channelId || r.channel_id,
        gross: r.gross,
        net: r.net,
        cpc: r.cpc,
        campaignFee: r.campaignFee || 0,
        coFund: r.coFund || 0,
        commission: r.commission, // null = uses channel default (captured below)
        cardFeePct: r.cardFeePct, // null = uses channel default (captured below)
      }));
      const channelRates = Object.values(ws?.channels || {}).map(c => ({
        id: c.id,
        commission: c.commission,
        cardFeePct: c.cardFeePct,
      }));
      const fingerprint = computeDataFingerprint({
        rows: fpRows,
        vatRate: ws?.vatRate,
        cogsPct: ws?.cogsPct,
        fixedMonthly: totalFixedMonthly,
        periodMode,
        channelRates,
        cpcTotal: t.cpc, // full period CPC from allocations — re-analyse when it changes (incl. days with no sales entry)
        coFundTotal: coFundTotal,
      });

      // 2) Check cache (within last 7 days, same fingerprint)
      if (!forceFresh && ws?.id) {
        try {
          const sevenDaysAgo = new Date(Date.now() - 7*86400000).toISOString();
          const { data: cached } = await getCachedAnalysis({
            wsId: ws.id, periodMode, fingerprint, chId, sinceISO: sevenDaysAgo,
          });
          if (cached?.analysis_text) {
            setAiInsight(cached.analysis_text);
            setAiCachedAt(cached.generated_at);
            return;
          }
        } catch (cacheErr) {
          console.warn('Cache lookup failed, falling back to fresh AI call:', cacheErr);
        }
      }

      // Compute period-allocated fixed costs (so analysis matches the time window)
      const aiPeriodDays = (() => {
        if (allRows.length === 0) return 30;
        const dates = allRows.map(r => r.dateObj?.getTime()).filter(t => !isNaN(t));
        if (dates.length === 0) return 30;
        const span = (Math.max(...dates) - Math.min(...dates)) / 86400000 + 1;
        return Math.max(1, Math.round(span));
      })();
      const aiPeriodFixed = totalFixedMonthly * ((cpcWinFrom && cpcWinTo) ? monthsForSpan(cpcWinFrom, cpcWinTo) : aiPeriodDays / 30.4375);
      const aiTrueProfitAfterFixed = (t.profit || 0) - aiPeriodFixed;
      const aiBreakEvenDailyGross = aiPeriodDays > 0 && contribPct > 0
        ? (aiPeriodFixed / aiPeriodDays) / contribPct  // daily fixed on the SAME basis as aiPeriodFixed
        : 0;
      const aiAvgDailyGross = aiPeriodDays > 0 ? (t.gross || 0) / aiPeriodDays : 0;
      const aiCoverageRatio = aiBreakEvenDailyGross > 0
        ? aiAvgDailyGross / aiBreakEvenDailyGross
        : 0;

      // ── Daily series (for trend attribution: why were some days better?) ──
      // Group rows by date so the AI can correlate profit with discount/CPC moves.
      const dayMap = {};
      allRows.forEach(r => {
        const d = r.date || (r.dateObj instanceof Date ? r.dateObj.toISOString().slice(0,10) : "?");
        if (!dayMap[d]) dayMap[d] = { gross:0, net:0, cpc:0, profit:0, coFund:0 };
        dayMap[d].gross += r.gross || 0;
        dayMap[d].net += r.net || r.gross || 0;
        dayMap[d].cpc += r.cpc || 0;
        dayMap[d].profit += r.trueProfit || 0;
        dayMap[d].coFund += r.coFund || 0;
      });
      const dailySeries = Object.entries(dayMap)
        .sort((a,b) => a[0].localeCompare(b[0]))
        .slice(-14) // cap to last 14 days to keep the prompt compact
        .map(([d,v]) => {
          const disc = v.gross > 0 ? ((v.gross - v.net)/v.gross*100) : 0;
          return `${d}: ${sym}${v.gross.toFixed(0)} gross, ${sym}${v.profit.toFixed(0)} profit, ${disc.toFixed(0)}% disc, ${sym}${v.cpc.toFixed(0)} CPC${v.coFund > 0 ? `, ${sym}${v.coFund.toFixed(2)} platform co-funding` : ""}`;
        }).join("\n");
      const dailySeriesBlock = Object.keys(dayMap).length >= 3
        ? dailySeries
        : "Not enough distinct days for trend analysis (need 3+ days of data).";


      const platformDetail = perPlatform.map(p => {
        const discPct = p.gross > 0 ? ((p.gross - p.net) / p.gross) * 100 : 0;
        const profitPerCpc = p.cpc > 0 ? p.profit / p.cpc : null; // £ profit per £ CPC
        return `${p.name}: ${sym}${p.gross.toFixed(0)} gross, ${sym}${p.net.toFixed(0)} net, ${sym}${p.profit.toFixed(0)} profit (${(p.profitPct*100).toFixed(1)}%), ${discPct.toFixed(0)}% avg discount${p.coFund > 0 ? ` (platform co-funded ${sym}${p.coFund.toFixed(2)}, true net ticket ${sym}${(p.net + p.coFund).toFixed(2)})` : ""}, ${sym}${p.cpc.toFixed(0)} CPC (${(p.cpcPctOfNet*100).toFixed(1)}% of ${p.coFund > 0 ? "true net ticket" : "net"})${profitPerCpc != null ? `, ${sym}${profitPerCpc.toFixed(2)} profit per ${sym}1 CPC` : ", no CPC spend"}`;
      }).join("\n");

      // 3) No cache hit — build prompt and call Claude
      const summary = {
        period: rangeLabel,
        periodDays: aiPeriodDays,
        currency,
        location: ws?.name || "Workspace",
        gross: t.gross.toFixed(2),
        net: t.net.toFixed(2),
        netReceivable: t.netReceivable.toFixed(2),
        commission: t.commission.toFixed(2),
        commissionPct: t.gross > 0 ? (t.commission/t.gross*100).toFixed(1) : 0,
        cardFees: t.cardFees.toFixed(2),
        cpc: t.cpc.toFixed(2),
        cpcPctOfNet: (cpcPctOfNet*100).toFixed(1),
        campaignFee: t.campaignFee.toFixed(2),
        vat: t.vat.toFixed(2),
        cogs: t.cogs.toFixed(2),
        cogsPct: ((ws?.cogsPct ?? 0) * 100).toFixed(1),
        vatPct: ((ws?.vatRate ?? 0) * 100).toFixed(1),
        trueProfit: t.profit.toFixed(2),
        avgDiscountPct: (avgDiscountPct*100).toFixed(1),
        coFund: coFundTotal.toFixed(2),
        netEff: netEff.toFixed(2),                                  // true net ticket (net + co-fund)
        ownDiscountPct: (ownDiscountPct*100).toFixed(1),
        cardFeePctOfNet: (cardFeePctOfNet*100).toFixed(2),          // on netEff (step 4)
        receivablePctOfGross: (receivablePctOfGross*100).toFixed(1),
        contribPct: (contribPct*100).toFixed(1),
        // Fixed cost context
        fixedMonthly: totalFixedMonthly.toFixed(2),
        periodFixed: aiPeriodFixed.toFixed(2),
        trueProfitAfterFixed: aiTrueProfitAfterFixed.toFixed(2),
        breakEvenDailyGross: aiBreakEvenDailyGross.toFixed(2),
        avgDailyGross: aiAvgDailyGross.toFixed(2),
        coverageRatio: (aiCoverageRatio * 100).toFixed(0),
        platforms: perPlatform.map(p => `${p.name}: ${sym}${p.gross.toFixed(0)} gross, ${sym}${p.profit.toFixed(0)} profit (${(p.profitPct*100).toFixed(1)}%)`).join("; "),
      };

      // "% of net" ratios are on the TRUE net ticket (net + platform co-funding)
      // whenever co-funding exists — commission and card fees are charged on it.
      const netBasisLabel = coFundTotal > 0 ? "true net ticket" : "net sales";
      const coFundLines = coFundTotal > 0
        ? `\n- Platform co-funding (the platform's share of discounts): ${sym}${summary.coFund}`
          + `\n- True net ticket (net sales + platform co-funding; commission and card fees are charged on this): ${sym}${summary.netEff}`
          + `\n- Restaurant-funded discount (after platform co-funding): ${summary.ownDiscountPct}% of gross`
        : "";

      const prompt = `You are a restaurant P&L analyst. Analyze this delivery restaurant's financial data using STRICT classification rules. Same data must always produce the same analysis.

INPUT DATA:
Restaurant: ${summary.location}
Period: ${summary.period}
Currency: ${currency}

Financials:
- Gross sales: ${sym}${summary.gross}
- Net sales (after discounts): ${sym}${summary.net}
- Average discount: ${summary.avgDiscountPct}%${coFundLines}
- Aggregator commission: ${sym}${summary.commission} (${summary.commissionPct}% of gross)
- Card processing fees: ${sym}${summary.cardFees} (${summary.cardFeePctOfNet}% of ${netBasisLabel})
- CPC ad spend: ${sym}${summary.cpc} (${summary.cpcPctOfNet}% of ${netBasisLabel})
- Campaign fees: ${sym}${summary.campaignFee}
- VAT paid: ${sym}${summary.vat}
- VAT breakdown — VAT on commission: ${sym}${(t.vatOnComm||0).toFixed(2)}, VAT on card fees: ${sym}${(t.vatOnCard||0).toFixed(2)}, VAT on CPC: ${sym}${(t.vatOnCpc||0).toFixed(2)}
- COGS (${summary.cogsPct}% of gross): ${sym}${summary.cogs}
- TRUE PROFIT (contribution, BEFORE fixed costs): ${sym}${summary.trueProfit}
- Receivable as % of gross: ${summary.receivablePctOfGross}%
- Contribution margin: ${summary.contribPct}%

Fixed cost context (rent + wages + utilities + other):
- Fixed costs per month${chId ? ' (allocated to this channel based on its revenue share)' : ' (workspace total)'}: ${sym}${summary.fixedMonthly}
- Period length: ${summary.periodDays} days
- Fixed allocated to period: ${sym}${summary.periodFixed}
- True profit AFTER fixed costs deducted: ${sym}${summary.trueProfitAfterFixed}
- Daily gross needed to break even: ${sym}${summary.breakEvenDailyGross}
- Average daily gross actual: ${sym}${summary.avgDailyGross}
- Break-even coverage: ${summary.coverageRatio}% (100% = exactly covering, <100% = losing money on fixed)

Per-platform breakdown:
${summary.platforms}

Per-platform detail (for CPC/discount trade-off and reallocation analysis):
${platformDetail}

Daily series (most recent days — for trend analysis):
${dailySeriesBlock}

═══════════════════════════════════════════════════════
STRICT RULES (apply in this exact order):
═══════════════════════════════════════════════════════

RULE 0 — OUTPUT DISCIPLINE (highest priority): Do all reasoning silently. Write ONLY final conclusions. NEVER write "wait", "actually", "let me reconsider", "— wait", or any phrase that corrects or second-guesses yourself mid-sentence. If a metric clears a threshold, simply state it clears the threshold — do not first claim the opposite. Every sentence in the output must be a clean, final statement.

VERDICT — driven by FIXED-COST COVERAGE first (whether the business actually makes money after rent, wages and utilities). Margin % is context, NOT the verdict. Pick ONE, checking in this exact order:
- Net sales > Gross sales (negative discount %) → "Data quality issue — verdict unreliable until data is corrected"
- True profit (pre-fixed) < 0 → "Losing money on variable costs"
- Fixed costs per month is 0 (coverage cannot be computed): if true profit (pre-fixed) > 0 → "Profitable — add your fixed costs (rent, wages) for a full risk read", otherwise → "Losing money on variable costs"
- True profit AFTER fixed costs < 0 (break-even coverage < 100%) → "Not yet covering fixed costs"
- Break-even coverage 100–119% → "Profitable but fragile — fixed costs only just covered"
- Break-even coverage 120–199% → "Healthy — profitable after all costs"
- Break-even coverage ≥ 200% → "Excellent — covering fixed costs twice over"
NEVER call the business "high risk" or "barely profitable" on margin percentage alone. For this delivery-aggregator model a low-teens (or single-digit) net margin is normal; what determines risk is whether fixed costs are covered.

BIGGEST ISSUE — pick the FIRST that matches (in this exact order):
1. Net sales > Gross sales (negative discount %) → "Data quality issue — net should never exceed gross. Verify entries."
2. True profit (pre-fixed) < 0 → "Operating at a loss — variable costs exceed revenue"
3. True profit AFTER fixed costs < 0 (but pre-fixed > 0) → "Variable costs are covered but fixed costs aren't. Coverage at X%. Need to scale revenue or cut fixed costs."
4. Aggregator commission > 32% of gross → "Aggregator commission is excessive at X%. Industry standard is 25-30%."
5. CPC > 8% of net (use the CPC % given above — it is of the true net ticket when one is listed) → "CPC ad spend is too high at X% of net. Industry healthy range is 3-6%."
6. Restaurant-funded discount > 15% of gross (use "Restaurant-funded discount" when it is listed, otherwise "Average discount") → "Discounting is heavy at X%. Reduces margin without proportional volume gain."
7. Card fees > 3% of net (use the card-fee % given above — it is of the true net ticket when one is listed; never recompute it from Net sales) → "Card fees above standard at X%. Renegotiate with processor."
8. True profit AFTER fixed costs > 0 but break-even coverage < 120% → "Fixed costs only X% covered — profitable but fragile to revenue dips. Build a buffer by lifting gross or trimming fixed costs."
9. Contribution margin < 8% → "Net margin is very thin at X% — little buffer if commission, COGS or ad costs rise."
10. None of the above → "No critical issues. Focus on growth opportunities."

ACTIONS — give 3 concrete actions with EXACT numbers (£/AED amounts and %), each ≤ 25 words:
- Each action must reference a SPECIFIC metric from the data above
- Each action must quantify the £/AED impact if implemented
- Never say "consider" or "explore" — say "do X by Y%"

═══════════════════════════════════════════════════════
EXTENDED ANALYSIS (only use numbers given above; frame as ESTIMATES, never guarantees):
═══════════════════════════════════════════════════════

CPC-vs-DISCOUNT TRADE-OFF:
- Compare the cost of discounting you fund (gross − net, minus any platform co-funding) against CPC spend.
- If discount cost is high relative to CPC, model whether shifting some discount budget into CPC could lower total cost. Example logic: "Discount cost ${sym}1000 + CPC ${sym}200 = ${sym}1200. Reducing discount to 20% (~${sym}400) and raising CPC to ${sym}500 = ${sym}900 total — an estimated ${sym}300 saving with more ad exposure."
- Only present this if you have both discount and CPC figures. Frame as "estimated" — you cannot guarantee volume holds.

PER-PLATFORM REALLOCATION:
- Using "profit per ${sym}1 CPC" per platform, identify which platform converts ad spend most efficiently.
- Recommend shifting CPC budget toward the higher profit-per-CPC platform and away from the lower one, with rough amounts.
- Requires ≥2 platforms with CPC data. If not enough data, say "Need more cross-platform CPC data to compare reallocation."

DISCOUNT SWEET-SPOT:
- Look at avg discount % vs profit. If heavy discounting coincides with weak net, suggest a lower discount band to test (e.g. "ran 40%, lost on net → test 30% or 25%").
- Frame as a test recommendation, not a guarantee.

VAT BREAKDOWN REPORT:
- Itemise the VAT components from the data: VAT on commission, VAT on card fees, VAT on CPC, and the total. These are the input VAT amounts charged by aggregators.
- Separately note the VAT total ${sym}${summary.vat} which in this system is calculated on net receivable and is payable to the tax authority.
- Present as a clear list suitable for an accounts team. Do not invent a different VAT base.

PROFIT-TARGET FORECAST:
- If the data supports it, give a rough scenario for lifting profit (e.g. to hit a higher weekly profit, which discounts/CPC mix across platforms). Use ONLY the rates given.
- Clearly label "Scenario estimate — based on current rates, actual results will vary." Never promise a figure.

TREND ANALYSIS (from the daily series):
- Identify the best 1-2 days by profit and the worst, and attribute WHY using the data: was higher profit linked to lower discount, higher CPC, or higher gross? Cite the actual figures from the series.
- State whether the current discount/CPC approach is working or should change, based on which days performed best.
- If fewer than 3 days of data, write "Need at least 3 days of data to surface trends."

VAT BREAKDOWN REPORT:
- Itemise VAT components from the data: VAT total ${sym}${summary.vat}. Note that in this system VAT is on net receivable.
- State the VAT figure and that it is payable to the tax authority on net receivable. Do not invent a different VAT base.

═══════════════════════════════════════════════════════
OUTPUT FORMAT (use this exact markdown structure, no deviations):
═══════════════════════════════════════════════════════

## Period verdict
[1-2 sentences. Start with the verdict label from rules above.]

## Biggest issue
[Apply ONE rule from the BIGGEST ISSUE table above. Maximum 2 sentences.
- If a real issue fires (rules 1-7): state the issue, the number, and what it means.
- If rule 10 fires (no issues): write ONLY "No critical issues — all metrics within healthy ranges. Focus shifts to growth." Do NOT pad with explanations of why other metrics are fine. Do NOT add caveats or "however" sentences.]

## Top 3 actions
1. **[Verb]** [specific change with number]. Estimated impact: ${sym}[X] [monthly/over period].
2. **[Verb]** [specific change with number]. Estimated impact: ${sym}[X] [monthly/over period].
3. **[Verb]** [specific change with number]. Estimated impact: ${sym}[X] [monthly/over period].

## Watch next period
- [Specific metric to track + threshold]
- [Specific metric to track + threshold]

## CPC vs discount trade-off
[Apply the trade-off rule. If both discount and CPC data exist, give the estimated saving with the comparison. Otherwise write "Not enough discount/CPC data to model this yet."]

## Platform reallocation
[Apply the reallocation rule using profit-per-CPC. Name platforms and rough amounts, or state more data is needed.]

## Discount sweet-spot
[One recommended discount band to test, with reasoning from the data. Frame as a test.]

## Profit-target scenario
[A rough scenario for lifting profit, labelled "Scenario estimate". Or "Add more data for a reliable forecast." if data is thin.]

## Trends
[Best and worst days by profit with the WHY (discount/CPC/gross), and whether to keep or change the current approach. Or the "need 3+ days" line.]

## VAT summary
[List VAT on commission, VAT on card fees, VAT on CPC, and the total VAT ${sym}${summary.vat} payable on net receivable. Keep it a tidy list for the accounts team.]

═══════════════════════════════════════════════════════
Write professionally. No emojis. No fluff. No "consider exploring" — give direct instructions. Restaurant owner is busy.
State every conclusion directly and decisively. NEVER narrate your own reasoning, never write "wait", "actually", "let me reconsider", or correct yourself mid-sentence — work out the answer internally and write only the final, clean conclusion.

CRITICAL — DO NOT INVENT CALCULATIONS:
- Only cite numbers that are explicitly given to you in the data above.
- Do NOT compute "expected vs actual" comparisons unless you can show the exact formula and have all inputs.
- VAT in this system is calculated on NET RECEIVABLE (after commission/CPC/card fees deducted), NOT on gross sales. Do not flag VAT as "overpaid" by comparing it to a percentage of gross.
- Commission and card fees are calculated on NET TICKET (post-discount), not gross. When platform co-funding is listed, the net ticket is the "True net ticket" line (net sales + platform co-funding).
- COGS is calculated on GROSS sales at the configured rate.
- "True profit" in the Financials section is BEFORE fixed costs. The "True profit AFTER fixed costs" line in Fixed cost context shows the final P&L after rent/wages/utilities/other are deducted proportionally for the period.
- If you cannot verify a calculation with the inputs provided, don't make the claim.${arabicReplyInstruction()}`;

      const data = await callAnthropic({
        model: "claude-sonnet-4-6",
        max_tokens: 2500,
        temperature: 0,
        messages: [{role: "user", content: prompt}],
      });
      const text = data.content?.filter(b => b.type === "text").map(b => b.text).join("\n") || "";
      setAiInsight(text);
      setAiCachedAt(new Date().toISOString());

      // 4) Save to cache (best effort — failure here doesn't break the user)
      try {
        const authUser = await getCurrentUser();
        await saveAnalysisToCache({
          workspace_id: ws.id,
          channel_id: chId || null,
          period_mode: periodMode,
          data_fingerprint: fingerprint,
          analysis_text: text,
          generated_by: authUser?.id || null,
        });
      } catch (saveErr) {
        console.warn('Cache save failed (insight still shown):', saveErr);
      }
    } catch (e) {
      setAiError(e.message || "Failed to generate insight");
    }
  }

  // ── Report 2: Menu pricing ────────────────────────────────────────────
  // Produced by the SAME "Generate analysis" button, shown as its own report.
  // Per-item economics are computed deterministically in JS (the AI never does
  // the arithmetic), then summarised into a prompt. Covers: suggested selling
  // price for a 10–15% NET margin (after discount, commission, card fees, VAT
  // and a per-order CPC/platform allowance), most-profitable items, high-cost
  // items with discount-tier erosion, and the best platform per item by commission.
  async function runPricingAnalysis(forceFresh = false) {
    setAiPricingError("");
    setAiPricing("");
    setAiPricingCachedAt(null);
    try {
      const cpcAllowance = currency === "AED" ? 5 : currency === "INR" ? 100 : 1;     // per-order ad/platform cost (₹100 ≈ £1)
      const TARGET_MIN = 0.10, TARGET_MID = 0.125, TARGET_MAX = 0.15;
      const PRICE_TIERS = [0, 0.20, 0.30, 0.50];           // discount tiers to model
      const vatRate = ws?.vatRate ?? cfg.vatRate ?? 0;
      const scopeLabel = chId && ws?.channels?.[chId] ? ws.channels[chId].platform : "all platforms";

      // Closed-form price that yields targetMargin (= profit ÷ price), matching
      // calcItem's formula exactly (VAT always on fees) plus the CPC allowance.
      // Returns null when discount + commission leave no room to hit the target.
      const suggestPrice = (cost, disc, commission, cardFeePct, fee, targetMargin) => {
        const k = (commission || 0) + (cardFeePct || 0);
        const denom = (1 - (disc || 0)) * (1 - k * (1 + vatRate)) - targetMargin;
        if (denom <= 0) return null;
        const numer = (fee || 0) * (1 + vatRate) + (cost || 0) + cpcAllowance;
        return numer / denom;
      };

      // Menu items in scope (respects the pill: selected channel, or all).
      const menuSourceChannels = chId && ws?.channels?.[chId]
        ? [ws.channels[chId]]
        : Object.values(ws?.channels || {});
      const allChannels = Object.values(ws?.channels || {});
      const menuRows = menuSourceChannels.flatMap(c =>
        (c.menu || []).map(m => ({
          id: m.id, name: m.name, cost: m.cost || 0, price: m.price || 0,
          disc: m.disc || 0, fee: m.fee || 0, channelId: c.id, platform: c.platform,
          commission: m.commission ?? c.commission ?? cfg.aggCommission,
          cardFeePct: m.cardFeePct ?? c.cardFeePct ?? 0,
        }))
      );

      if (menuRows.length === 0) {
        setAiPricing(`No menu items configured for ${scopeLabel}. Add items in the **Price Tool** to unlock suggested pricing, discount-tier analysis, and per-platform recommendations.`);
        return;
      }

      // Every platform's rates, for cross-platform "best platform per item".
      const platforms = allChannels.map(c => ({
        id: c.id, name: c.platform,
        commission: c.commission ?? cfg.aggCommission,
        cardFeePct: c.cardFeePct ?? 0,
      }));

      // Per-item, fully-computed economics (profit figures include the CPC allowance).
      const itemsData = menuRows.map(m => {
        const comm = m.commission, card = m.cardFeePct;
        const ownCfg = { aggCommission: comm, vatRate, cogsPct: ws?.cogsPct ?? cfg.cogsPct, vatOnFees: ws?.vatOnFees === true };
        const cur = calcItem(m.cost, m.price, m.disc, m.fee, ownCfg, card);
        const curProfit = +(cur.profit - cpcAllowance).toFixed(2);
        const curMargin = m.price > 0 ? curProfit / m.price : 0;
        const tierRows = PRICE_TIERS.map(d => {
          const rr = calcItem(m.cost, m.price, d, m.fee, ownCfg, card);
          const p = +(rr.profit - cpcAllowance).toFixed(2);
          return { disc: d * 100, profit: p, margin: m.price > 0 ? p / m.price : 0, suggestedPrice: suggestPrice(m.cost, d, comm, card, m.fee, TARGET_MID) };
        });
        const byPlatform = platforms.map(pf => {
          const r = calcItem(m.cost, m.price, m.disc, m.fee, { aggCommission: pf.commission, vatRate, cogsPct: ws?.cogsPct ?? cfg.cogsPct, vatOnFees: ws?.vatOnFees === true }, pf.cardFeePct);
          const p = +(r.profit - cpcAllowance).toFixed(2);
          return { platform: pf.name, commission: pf.commission, profit: p, margin: m.price > 0 ? p / m.price : 0 };
        }).sort((a, b) => b.profit - a.profit);
        return {
          name: m.name, platform: m.platform, cost: m.cost, price: m.price, disc: (m.disc || 0) * 100,
          curProfit, curMargin, tiers: tierRows, byPlatform,
          spMin: suggestPrice(m.cost, m.disc, comm, card, m.fee, TARGET_MIN),
          spMid: suggestPrice(m.cost, m.disc, comm, card, m.fee, TARGET_MID),
          spMax: suggestPrice(m.cost, m.disc, comm, card, m.fee, TARGET_MAX),
        };
      });

      // Cache (same table, discriminated by period_mode="pricetool").
      const channelRates = allChannels.map(c => ({ id: c.id, commission: c.commission, cardFeePct: c.cardFeePct }));
      const fingerprint = computeMenuFingerprint({
        items: menuRows.map(m => ({ id: m.id, name: m.name, cost: m.cost, price: m.price, disc: m.disc, fee: m.fee, channelId: m.channelId })),
        vatRate, cogsPct: ws?.cogsPct ?? cfg.cogsPct, currency, channelRates,
      });
      if (!forceFresh && ws?.id) {
        try {
          const sevenDaysAgo = new Date(Date.now() - 7 * 86400000).toISOString();
          const { data: cached } = await getCachedAnalysis({ wsId: ws.id, periodMode: "pricetool", fingerprint, chId, sinceISO: sevenDaysAgo });
          if (cached?.analysis_text) {
            setAiPricing(cached.analysis_text);
            setAiPricingCachedAt(cached.generated_at);
            return;
          }
        } catch (cacheErr) { console.warn("Pricing cache lookup failed:", cacheErr); }
      }

      // Compact, deterministic data blocks for the prompt.
      const fmtP = n => n == null ? "n/a" : `${sym}${(+n).toFixed(2)}`;
      const byCostDesc = [...itemsData].sort((a, b) => b.cost - a.cost);
      const byMarginDesc = [...itemsData].sort((a, b) => b.curMargin - a.curMargin);
      const cap = 14;                                       // keep the prompt compact
      const detail = byCostDesc.slice(0, cap).map(it => {
        const tierLine = it.tiers.map(tt => `${tt.disc}%→profit ${fmtP(tt.profit)} (${(tt.margin * 100).toFixed(0)}% margin), price-for-12.5%=${fmtP(tt.suggestedPrice)}`).join("; ");
        const platLine = it.byPlatform.map(pp => `${pp.platform} (${(pp.commission * 100).toFixed(0)}% comm)→${fmtP(pp.profit)} (${(pp.margin * 100).toFixed(0)}%)`).join(", ");
        return `• ${it.name} [${it.platform}] cost ${fmtP(it.cost)}, price ${fmtP(it.price)}, current disc ${it.disc.toFixed(0)}%, current profit ${fmtP(it.curProfit)} (${(it.curMargin * 100).toFixed(0)}% net margin).\n   Suggested price @ current disc for 10–15% margin: ${fmtP(it.spMin)} (10%) – ${fmtP(it.spMax)} (15%), midpoint ${fmtP(it.spMid)}.\n   Discount tiers: ${tierLine}.\n   Per-platform profit (current disc): ${platLine}.`;
      }).join("\n");
      const truncatedNote = itemsData.length > cap ? `\n(Showing the ${cap} highest-cost items of ${itemsData.length} total.)` : "";
      const highCostList = byCostDesc.slice(0, 6).map(it => `${it.name} (cost ${fmtP(it.cost)}, ${(it.curMargin * 100).toFixed(0)}% margin at ${it.disc.toFixed(0)}% disc)`).join("; ");
      const topMarginList = byMarginDesc.slice(0, 6).map(it => `${it.name} [${it.platform}] ${(it.curMargin * 100).toFixed(0)}% margin, ${fmtP(it.curProfit)}/order`).join("; ");
      const platformRates = platforms.map(p => `${p.name}: ${(p.commission * 100).toFixed(0)}% commission, ${(p.cardFeePct * 100).toFixed(2)}% card fee`).join("; ");

      const pricingPrompt = `You are a restaurant menu-pricing analyst for a delivery business. Use ONLY the pre-computed numbers below — never invent or recompute figures. The same data must always produce the same analysis.

CONTEXT
- Currency: ${currency} (${sym.trim()})
- Scope: ${scopeLabel}
- VAT rate applied to fees: ${(vatRate * 100).toFixed(1)}%
- Per-order CPC/platform allowance already deducted from every profit figure below: ${sym}${cpcAllowance.toFixed(2)}
- Target NET margin band (profit ÷ selling price, after discount, commission, card fees, VAT and the CPC allowance): 10%–15%
- Platform rates: ${platformRates}

MOST PROFITABLE ITEMS (by current net margin): ${topMarginList || "none"}
HIGHEST-COST ITEMS (discount these the least): ${highCostList || "none"}

PER-ITEM DATA (highest cost first):${truncatedNote}
${detail}

═══════════════════════════════════════
WRITE THE REPORT IN THIS EXACT MARKDOWN STRUCTURE. No preamble. Professional, decisive, no emojis. Every number you cite must come verbatim from the data above.

## Suggested selling prices (10–15% net margin)
For each item (or the clearest 6–8 if there are many), state the recommended selling price to land inside the 10–15% net-margin band AT ITS CURRENT DISCOUNT, using the "Suggested price @ current disc" range. Flag any item whose current price is BELOW its 10% suggestion as underpriced and quantify the gap. If a suggested price shows "n/a", say the discount is too deep to reach the target and a lower discount is required.

## Most profitable items to push
Rank the highest-margin items and tell the owner which 2–3 to feature or advertise, citing each one's margin and per-order profit.

## High-cost items — keep discounts low
List the highest-cost items. For each, show how profit erodes across the 20%, 30% and 50% discount tiers (cite the tier profit figures) and state the maximum discount you would allow before margin falls below ~10%. Be explicit that deep discounts on these items destroy profit fastest.

## Best platform per item
Using the per-platform profit figures (which reflect each platform's commission), name the platform that returns the most profit for each of the top items, and where to steer volume.

## Quick wins
Three specific, numbered actions with exact ${sym} amounts (e.g. "Raise X from ${sym}A to ${sym}B to reach a 12% margin"). Never say "consider" — give the instruction.${arabicReplyInstruction()}`;

      const pricingData = await callAnthropic({
        model: "claude-sonnet-4-6",
        max_tokens: 2500,
        temperature: 0,
        messages: [{ role: "user", content: pricingPrompt }],
      });
      const pricingText = pricingData.content?.filter(b => b.type === "text").map(b => b.text).join("\n") || "";
      setAiPricing(pricingText);
      setAiPricingCachedAt(new Date().toISOString());

      try {
        const authUser = await getCurrentUser();
        await saveAnalysisToCache({
          workspace_id: ws.id,
          channel_id: chId || null,
          period_mode: "pricetool",
          data_fingerprint: fingerprint,
          analysis_text: pricingText,
          generated_by: authUser?.id || null,
        });
      } catch (saveErr) { console.warn("Pricing cache save failed:", saveErr); }
    } catch (e) {
      setAiPricingError(e.message || "Failed to generate pricing analysis");
    }
  }

  // One click → both reports, in parallel, sharing the loading flag.
  async function generateAnalyses(forceFresh = false) {
    setAiLoading(true);
    try {
      await Promise.all([runPeriodAnalysis(forceFresh), runPricingAnalysis(forceFresh)]);
    } finally {
      setAiLoading(false);
    }
  }

  if (!ws || allRows.length === 0) {
    // Check if data exists in any period (not just current selected one)
    const channelsAll = chId && ws?.channels?.[chId] ? [ws.channels[chId]] : Object.values(ws?.channels || {});
    const totalAvailableEntries = channelsAll.reduce((n, c) => n + (c.history?.length || 0), 0);
    const hasDataElsewhere = totalAvailableEntries > 0;

    // Find the most recent entry date across all channels
    let mostRecent = null;
    channelsAll.forEach(c => (c.history || []).forEach(r => {
      if (r.dateObj instanceof Date && (!mostRecent || r.dateObj > mostRecent)) mostRecent = r.dateObj;
    }));
    const daysSinceLast = mostRecent ? Math.floor((Date.now() - mostRecent.getTime()) / 86400000) : null;

    return (
      <div className="nv-page" style={{padding:24}}>
        <SectionHdr
          title={InsightPlusTitle}
          sub={`${ws?.name || "Workspace"} · ${rangeLabel}`}
        />
        <Card>
          {periodMode === "custom" && (!customFrom || !customTo) ? (
            <EmptyState icon="calendar" title={tr("analysis.empty.pickRange.title")}
              message={tr("analysis.empty.pickRange.msg")} />
          ) : hasDataElsewhere ? (
            <EmptyState icon="bars" title={tr("analysis.empty.noPeriodData.title")}
              message={<>{tr("analysis.empty.noPeriodData.youHave")}<strong style={{color:C.text}}>{totalAvailableEntries}{tr("analysis.empty.noPeriodData.entriesSuffix")}</strong>{tr("analysis.empty.noPeriodData.butNoneIn")}{rangeLabel.toLowerCase()}.{daysSinceLast !== null && daysSinceLast > 0 && <>{tr("analysis.empty.noPeriodData.recentEntryWas")}<strong style={{color:C.text}}>{daysSinceLast} day{daysSinceLast === 1 ? "" : "s"}{tr("analysis.empty.noPeriodData.ago")}</strong>.</>}{tr("analysis.empty.noPeriodData.widenHint")}<strong style={{color:C.sub}}>{tr("analysis.empty.noPeriodData.allOption")}</strong>.</>} />
          ) : (
            <EmptyState icon="bars" title={tr("analysis.empty.noData.title")}
              message={tr("analysis.empty.noData.msg")} />
          )}
        </Card>
      </div>
    );
  }

  return (
    <div className="nv-page" style={{padding:24}}>
      <SectionHdr
        title={InsightPlusTitle}
        sub={`${ws?.name || "Workspace"} · ${rangeLabel} · ${allRows.length}${tr("analysis.hdr.logEntriesSuffix")}`}
      />

      {/* Platform contributions failed to load (db.js sets ws.coFundLoadFailed):
          every figure below falls back to the raw net, so say so. */}
      {ws?.coFundLoadFailed === true && (
        <div role="status" style={{padding:"10px 14px",marginBottom:16,background:C.yellow10,border:`1px solid ${C.yellow}55`,borderLeft:`3px solid ${C.yellow}`,borderRadius:7,fontSize:12.5,color:"#7a4f00",lineHeight:1.55,fontWeight:500}}>
          {tr("analysis.warn.coFundLoadFailed")}
        </div>
      )}

      {/* Analysis — top of page */}
      <Card sx={{marginBottom:16}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:14,flexWrap:"wrap",gap:12}}>
          <div>
            <div style={{fontSize:15,fontWeight:700,color:C.text,letterSpacing:"-.3px"}}>{tr("analysis.period.title")}</div>
            <div style={{fontSize:12.5,color:C.muted,marginTop:4,fontWeight:500}}>{tr("analysis.period.sub")}</div>
          </div>
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            {aiInsight && (
              <Btn v="secondary" onClick={()=>generateInsightsPDF({
                wsName: ws?.name || "Workspace",
                period: rangeLabel,
                currency,
                sym,
                totals: t,
                perPlatform,
                aiText: aiInsight,
                profitPctOfGross,
                contribPct,
                avgDiscountPct,
                receivablePctOfGross,
                cpcPctOfNet,
                vatRate: ws?.vatRate ?? 0,
                cogsPct: ws?.cogsPct ?? 0,
                coverageRatio,
                trueProfitAfterFixed,
                hasFixedCosts,
              })}>{tr("analysis.btn.downloadPdf")}</Btn>
            )}
            {paidAccess ? (
              <Btn onClick={()=>generateAnalyses()} disabled={aiLoading}>
                {aiLoading ? tr("analysis.btn.analyzing") : (aiInsight || aiPricing) ? tr("analysis.btn.refresh") : tr("analysis.btn.generateInsight")}
              </Btn>
            ) : (
              /* Trial accounts: AI insight is a paid feature — greyed, opens the
                 subscribe modal instead of calling the model. */
              <button onClick={()=>onShowUpgrade && onShowUpgrade()} title={tr("analysis.btn.lockedInsightHint")}
                style={{padding:"9px 14px",borderRadius:8,border:`1px solid ${C.border}`,background:C.bg3,color:C.muted,cursor:"pointer",fontSize:13,fontWeight:600,fontFamily:"inherit",display:"inline-flex",alignItems:"center",gap:7,opacity:.85}}>
                <span aria-hidden="true">🔒</span>{tr("analysis.btn.generateInsight")}<span style={{fontWeight:500}}>· {tr("analysis.btn.lockedInsight")}</span>
              </button>
            )}
          </div>
        </div>
        {aiError && (
          <div style={{padding:"10px 12px",background:C.red10,border:`1px solid ${C.red}33`,borderRadius:7,fontSize:12,color:C.redDark,marginTop:10}}>
            {aiError}
          </div>
        )}
        {aiLoading && !aiInsight && (
          <div style={{padding:"28px 20px",textAlign:"center",background:C.bg3,borderRadius:9,border:`1px solid ${C.border2}`}}>
            <div style={{display:"inline-flex",alignItems:"center",gap:10}}>
              <div style={{width:14,height:14,border:`2px solid ${C.border}`,borderTopColor:C.blue,borderRadius:8,animation:"spin 0.8s linear infinite"}}/>
              <div style={{fontSize:13,color:C.muted,fontWeight:500}}>{tr("analysis.loading.readingNumbers")}</div>
            </div>
            <div style={{fontSize:11,color:C.muted2,marginTop:6,fontWeight:500}}>{tr("analysis.loading.usuallyTakes")}</div>
          </div>
        )}
        {aiInsight && (
          <div style={{padding:"22px 24px",background:"#fff",border:`1px solid ${C.border2}`,borderLeft:`3px solid ${C.blue}`,borderRadius:8}}>
            {aiCachedAt && (
              <div style={{fontSize:11,color:C.muted2,marginBottom:14,display:"flex",alignItems:"center",gap:6,fontWeight:500}}>
                <span style={{width:5,height:5,borderRadius:3,background:C.green}}/>
                {tr("analysis.generatedPrefix")}{formatRelativeTime(aiCachedAt)}{tr("analysis.period.generatedFromData")}
              </div>
            )}
            <MarkdownText text={aiInsight}/>
          </div>
        )}
        {!aiInsight && !aiLoading && !aiError && (
          <div style={{padding:"14px 16px",background:C.bg3,border:`1px dashed ${C.border}`,borderRadius:9,fontSize:12.5,color:C.muted,lineHeight:1.6,fontWeight:500}}>
            {tr("analysis.period.hint.clickPrefix")}<strong style={{color:C.text}}>{tr("analysis.btn.generateInsight")}</strong>{tr("analysis.period.hint.forTwoReports")}<strong style={{color:C.text}}>{tr("analysis.period.hint.periodPnl")}</strong>{tr("analysis.period.hint.readMid")}<strong style={{color:C.text}}>{tr("analysis.period.hint.menuPricing")}</strong>{tr("analysis.period.hint.readSuffix")}
          </div>
        )}
      </Card>

      {/* Menu pricing — second report produced by the same "Generate analysis" button */}
      <Card sx={{marginBottom:16}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:14,flexWrap:"wrap",gap:12}}>
          <div>
            <div style={{fontSize:15,fontWeight:700,color:C.text,letterSpacing:"-.3px"}}>{tr("analysis.pricing.title")}</div>
            <div style={{fontSize:12.5,color:C.muted,marginTop:4,fontWeight:500}}>{tr("analysis.pricing.sub")}</div>
          </div>
          {aiPricing && (
            <Btn v="secondary" onClick={()=>generatePricingPDF({ wsName: ws?.name || "Workspace", scopeLabel: chId && ws?.channels?.[chId] ? ws.channels[chId].platform : "All platforms", currency, sym, aiText: aiPricing })}>{tr("analysis.btn.downloadPdf")}</Btn>
          )}
        </div>
        {aiPricingError && (
          <div style={{padding:"10px 12px",background:C.red10,border:`1px solid ${C.red}33`,borderRadius:7,fontSize:12,color:C.redDark,marginTop:10}}>
            {aiPricingError}
          </div>
        )}
        {aiLoading && !aiPricing && (
          <div style={{padding:"28px 20px",textAlign:"center",background:C.bg3,borderRadius:9,border:`1px solid ${C.border2}`}}>
            <div style={{display:"inline-flex",alignItems:"center",gap:10}}>
              <div style={{width:14,height:14,border:`2px solid ${C.border}`,borderTopColor:C.blue,borderRadius:8,animation:"spin 0.8s linear infinite"}}/>
              <div style={{fontSize:13,color:C.muted,fontWeight:500}}>{tr("analysis.loading.pricingMenu")}</div>
            </div>
            <div style={{fontSize:11,color:C.muted2,marginTop:6,fontWeight:500}}>{tr("analysis.loading.usuallyTakes")}</div>
          </div>
        )}
        {aiPricing && (
          <div style={{padding:"22px 24px",background:"#fff",border:`1px solid ${C.border2}`,borderLeft:`3px solid ${C.blue}`,borderRadius:8}}>
            {aiPricingCachedAt && (
              <div style={{fontSize:11,color:C.muted2,marginBottom:14,display:"flex",alignItems:"center",gap:6,fontWeight:500}}>
                <span style={{width:5,height:5,borderRadius:3,background:C.green}}/>
                {tr("analysis.generatedPrefix")}{formatRelativeTime(aiPricingCachedAt)}{tr("analysis.pricing.generatedFromMenu")}
              </div>
            )}
            <MarkdownText text={aiPricing}/>
          </div>
        )}
        {!aiPricing && !aiLoading && !aiPricingError && (
          <div style={{padding:"14px 16px",background:C.bg3,border:`1px dashed ${C.border}`,borderRadius:9,fontSize:12.5,color:C.muted,lineHeight:1.6,fontWeight:500}}>
            {tr("analysis.pricing.hint.prefix")}<strong style={{color:C.text}}>{tr("analysis.btn.generateInsight")}</strong>{tr("analysis.pricing.hint.suffix")}
          </div>
        )}
      </Card>

      {/* Top KPIs */}
      <div className="nv-grid-md-2 nv-grid-2-sm" style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:18}}>
        {[
          [tr("analysis.kpi.grossSales"), `${sym}${t.gross.toFixed(2)}`, C.blueDark, null],
          [tr("analysis.kpi.netReceivable"), `${sym}${t.netReceivable.toFixed(2)}`, C.blueDark, tr("analysis.kpi.info.vsDailyLog")],
          [tr("analysis.kpi.trueProfit"), `${sym}${t.profit.toFixed(2)}`, posc(t.profit), tr("analysis.kpi.info.vsDailyLog")],
          [tr("analysis.kpi.profitMargin"), `${(profitPctOfGross*100).toFixed(1)}%`, posc(profitPctOfGross), null],
        ].map(([l,v,c,tip])=>(
          <div key={l} style={{padding:"16px 18px",background:"#fff",border:`1px solid ${C.border}`,borderRadius:10,position:"relative",overflow:tip?"visible":"hidden"}}>
            <div style={{position:"absolute",top:0,left:0,right:0,height:3,background:c}}/>
            <div style={{fontSize:10.5,color:C.muted,fontWeight:700,textTransform:"uppercase",letterSpacing:.6,marginBottom:6,marginTop:3,display:"flex",alignItems:"center",gap:5}}>{l}{tip && <InfoTip text={tip}/>}</div>
            <div className="num" style={{fontSize:22,fontWeight:800,color:c,letterSpacing:"-.5px"}}>{v}</div>
          </div>
        ))}
      </div>

      {/* Pie chart + breakdown table */}
      <div className="nv-grid-md-1 nv-grid-1-sm" style={{display:"grid",gridTemplateColumns:"1fr 1.4fr",gap:14,marginBottom:16}}>
        <Card>
          <div style={{fontSize:14,fontWeight:700,color:C.text,marginBottom:14,letterSpacing:"-.2px"}}>{tr("analysis.pie.titlePrefix")}{sym.trim()}{t.gross.toFixed(0)}{tr("analysis.pie.titleSuffix")}</div>
          <div style={{display:"flex",justifyContent:"center",marginBottom:12}}>
            <svg viewBox="0 0 200 200" style={{width:"100%",maxWidth:240,height:240}}>
              {piePaths.map((p,i) => (
                <path key={i} d={p.d} fill={p.seg.color} stroke="#fff" strokeWidth="1.5"/>
              ))}
              <circle cx="100" cy="100" r="38" fill="#fff"/>
              <text x="100" y="93" textAnchor="middle" fontSize="9" fill={C.muted} fontWeight="700" letterSpacing="0.5">{tr("analysis.pie.grossLabel")}</text>
              <text x="100" y="110" textAnchor="middle" fontSize="14" fill={C.text} fontWeight="800" fontFamily="Inter, sans-serif">{sym}{t.gross > 999 ? `${(t.gross/1000).toFixed(1)}k` : t.gross.toFixed(0)}</text>
            </svg>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:6}}>
            {piePaths.map((p,i) => (
              <div key={i} style={{display:"flex",alignItems:"center",gap:8,fontSize:12}}>
                <div style={{width:10,height:10,borderRadius:3,background:p.seg.color,flexShrink:0}}/>
                <span style={{flex:1,color:C.text,fontWeight:500}}>{p.seg.name}</span>
                <span className="num" style={{color:C.muted,fontWeight:600}}>{sym}{p.seg.value.toFixed(2)}</span>
                <span className="num" style={{color:C.muted2,fontWeight:600,minWidth:42,textAlign:"right"}}>{t.gross > 0 ? (p.seg.value/t.gross*100).toFixed(1) : "0.0"}%</span>
              </div>
            ))}
            {/* Net receivable is a SUBTOTAL, not one of the slices (the slices
                above sum to gross). Shown here as its share OF gross so the owner
                can see the percentage net receivable represents — without
                distorting the ring (a wedge would overlap the cost slices). */}
            <div style={{display:"flex",alignItems:"center",gap:8,fontSize:12,marginTop:4,paddingTop:8,borderTop:`1px solid ${C.border2}`}}>
              <div style={{width:10,height:10,borderRadius:3,border:`2px solid ${C.blueDark}`,background:"transparent",flexShrink:0}}/>
              <span style={{flex:1,color:C.text,fontWeight:600}}>{tr("analysis.kpi.netReceivable")}</span>
              <span className="num" style={{color:posc(t.netReceivable),fontWeight:700}}>{t.netReceivable < 0 ? "-" : ""}{sym}{Math.abs(t.netReceivable).toFixed(2)}</span>
              <span className="num" style={{color:C.muted2,fontWeight:600,minWidth:42,textAlign:"right"}}>{t.gross > 0 ? (t.netReceivable/t.gross*100).toFixed(1) : "0.0"}%</span>
            </div>
          </div>
        </Card>
        <Card>
          <div style={{fontSize:14,fontWeight:700,color:C.text,marginBottom:14,letterSpacing:"-.2px"}}>{tr("analysis.breakdown.title")}</div>
          <div className="nv-table-scroll">
          <table style={{width:"100%",fontSize:12.5,borderCollapse:"collapse"}}>
            <tbody>
              {[
                // 6th element `bg` pre-computes the row background from the stable
                // (language-independent) row semantics, so translating the label
                // text never changes the layout/colour logic.
                [tr("analysis.row.grossSales"), t.gross, C.blueDark, true, null, false, "transparent"],
                totalDiscount >= 0
                  ? [tr("analysis.row.discountsGiven"), -totalDiscount, C.red, false, t.gross > 0 ? `${(avgDiscountPct*100).toFixed(1)}%` : null, false, "transparent"]
                  : [tr("analysis.row.netExceedsGross"), totalDiscount, C.red, false, tr("analysis.note.dataError"), false, "transparent"],
                [tr("analysis.row.netSales"), t.net, C.blueDark, true, null, false, C.bg3],
                ...(coFundTotal > 0 ? [
                  [tr("analysis.row.platformCoFund"), coFundTotal, C.green, false, tr("analysis.note.coFunded"), false, "transparent"],
                  [tr("analysis.row.trueNetTicket"), netEff, C.blueDark, true, null, false, C.bg3],
                ] : []),
                [tr("analysis.row.aggregatorCommission"), -t.commission, C.red, false, t.gross > 0 ? `${(t.commission/t.gross*100).toFixed(1)}%` : null, false, "transparent"],
                ...((t.vatOnComm||0) > 0 ? [[tr("analysis.row.vatOnCommission"), -(t.vatOnComm||0), C.muted2, false, tr("analysis.note.recoverable"), true, "transparent"]] : []),
                [tr("analysis.row.cardFees"), -t.cardFees, C.red, false, `${(cardFeePctOfNet*100).toFixed(2)}%`, false, "transparent"],
                ...((t.vatOnCard||0) > 0 ? [[tr("analysis.row.vatOnCardFees"), -(t.vatOnCard||0), C.muted2, false, tr("analysis.note.recoverable"), true, "transparent"]] : []),
                [tr("analysis.row.cpcAdSpend"), -t.cpc, C.red, false, `${(cpcPctOfNet*100).toFixed(1)}%`, false, "transparent"],
                ...((t.vatOnCpc||0) > 0 ? [[tr("analysis.row.vatOnCpc"), -(t.vatOnCpc||0), C.muted2, false, tr("analysis.note.recoverable"), true, "transparent"]] : []),
                [tr("analysis.row.campaignFees"), -t.campaignFee, C.red, false, null, false, "transparent"],
                ...(t.subscriptionFee > 0 ? [[tr("analysis.row.subscriptionFee"), -t.subscriptionFee, C.red, false, t.gross > 0 ? `${(t.subscriptionFee/t.gross*100).toFixed(1)}%${tr("analysis.note.ofGrossSuffix")}` : null, false, "transparent"]] : []),
                ...((t.subscriptionVat||0) > 0 ? [[tr("analysis.row.vatOnSubscription"), -(t.subscriptionVat||0), C.muted2, false, tr("analysis.note.recoverable"), true, "transparent"]] : []),
                [tr("analysis.row.netReceivable"), t.netReceivable, C.blueDark, true, `${(receivablePctOfGross*100).toFixed(1)}%${tr("analysis.note.ofGrossSuffix")}`, false, C.bg3],
                [tr("analysis.row.vat"), -t.vat, C.red, false, `${((ws?.vatRate??0.20)*100).toFixed(0)}%${tr("analysis.note.onNetReceivableSuffix")}`, false, "transparent"],
                [tr("analysis.row.earningAfterVat"), t.eav, C.green, true, null, false, C.bg3],
                [tr("analysis.row.cogsFoodCost"), -t.cogs, C.red, false, `${((ws?.cogsPct??0.25)*100).toFixed(0)}%${tr("analysis.note.ofGrossSuffix")}`, false, "transparent"],
                [tr("analysis.row.trueProfit"), t.profit, posc(t.profit), true, t.gross > 0 ? `${(profitPctOfGross*100).toFixed(1)}%${tr("analysis.note.ofGrossSuffix")}` : null, false, C.green10],
              ].map(([l,v,c,bold,note,isSub,bg],i,arr)=>(
                <tr key={i} style={{borderBottom:i<arr.length-1?`1px solid ${C.border2}`:"none",background:bg}}>
                  <td style={{padding:isSub?"5px 10px 5px 22px":"8px 10px",fontWeight:bold?700:isSub?400:500,color:isSub?C.muted2:C.text,fontSize:isSub?11.5:undefined}}>{l}</td>
                  {/* Subtotal rows (bold "=" lines: Net receivable, Earning after
                      VAT, True profit) carry a real SIGNED total — show a clear
                      minus and turn red when negative, so a loss never reads as a
                      positive/green figure. Deduction rows (non-bold) keep showing
                      their magnitude; their label already carries the "−". */}
                  <td className="num" style={{padding:isSub?"5px 10px":"8px 10px",fontWeight:bold?700:isSub?400:500,color:(bold && v < 0)?C.red:c,textAlign:"right",fontVariantNumeric:"tabular-nums",fontSize:isSub?11.5:undefined}}>{bold && v < 0 ? "-" : ""}{sym}{Math.abs(v).toFixed(2)}</td>
                  <td style={{padding:"8px 10px",color:C.muted2,fontSize:11,textAlign:"right",fontWeight:500,minWidth:80}}>{note || ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </Card>
      </div>

      {/* Per-platform gross vs profit bar chart (AI doc: bar chart per platform) */}
      {perPlatform.length > 1 && !chId && (
        <Card sx={{marginBottom:16}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4,flexWrap:"wrap",gap:8}}>
            <div style={{fontSize:14,fontWeight:700,color:C.text,letterSpacing:"-.2px"}}>{tr("analysis.bar.title")}</div>
            {rangeLabel && (
              <div style={{display:"inline-flex",alignItems:"center",gap:6,background:C.blue10,border:`1px solid ${C.blue}33`,borderRadius:20,padding:"5px 12px"}}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={C.blue} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                <span style={{fontSize:11.5,color:C.blueDark,fontWeight:700,letterSpacing:"-.1px"}}>{rangeLabel}</span>
              </div>
            )}
          </div>
          <div style={{fontSize:12,color:C.muted,marginBottom:14,fontWeight:500}}>{tr("analysis.bar.sub")}</div>
          <ResponsiveContainer width="100%" height={230}>
            <BarChart data={perPlatform.map(p=>({name:p.name,Gross:+p.gross.toFixed(0),Profit:+p.profit.toFixed(0)}))} margin={{top:4,right:8,left:-8,bottom:0}} barGap={4}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border2} vertical={false}/>
              <XAxis dataKey="name" tick={{fontSize:11,fill:C.muted}} axisLine={{stroke:C.border}} tickLine={false}/>
              <YAxis tick={{fontSize:11,fill:C.muted}} axisLine={false} tickLine={false} width={48} tickFormatter={v=>`${sym}${v}`}/>
              <Tooltip formatter={(v,n)=>[`${sym}${Number(v).toLocaleString()}`,n==="Gross"?tr("analysis.bar.seriesGross"):n==="Profit"?tr("analysis.bar.seriesProfit"):n]} contentStyle={{fontSize:12,borderRadius:8,border:`1px solid ${C.border}`}}/>
              <Bar dataKey="Gross" fill={C.blue20} radius={[3,3,0,0]} barSize={22}/>
              <Bar dataKey="Profit" fill={C.blue} radius={[3,3,0,0]} barSize={22}/>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* Per-platform performance — only when viewing All channels */}
      {perPlatform.length >= 1 && !chId && (
        <Card sx={{marginBottom:16}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:14}}>
            <div>
              <div style={{fontSize:14,fontWeight:700,color:C.text,letterSpacing:"-.2px"}}>
                {perPlatform.length === 1 ? tr("analysis.platforms.titleSingle") : tr("analysis.platforms.titleMulti")}
              </div>
              <div style={{fontSize:12,color:C.muted,marginTop:4,fontWeight:500}}>
                {perPlatform.length === 1
                  ? tr("analysis.platforms.subSingle")
                  : tr("analysis.platforms.subMulti")}
              </div>
            </div>
          </div>
          <div className="nv-table-scroll" style={{overflowX:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
              <thead>
                <tr style={{borderBottom:`2px solid ${C.border}`}}>
                  {ppSortCols.map((sc,i)=>(
                    <SortableTh key={sc.key} col={sc} table={ppTable}
                      align={i===0?"left":"right"}
                      thStyle={{
                        padding:"10px 8px",
                        fontSize:10,fontWeight:700,color:C.muted,
                        textTransform:"uppercase",letterSpacing:.6
                      }}/>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ppTable.rows.length===0 && (
                  <tr><td colSpan={ppSortCols.length} style={{padding:"22px 8px",textAlign:"center",color:C.muted,fontSize:12}}>{tr("analysis.platforms.noMatch")}</td></tr>
                )}
                {ppTable.rows.map((p,i) => {
                  const isBest = p.id === bestPlatformId;
                  return (
                  <tr key={p.id || i} style={{
                    borderBottom:`1px solid ${C.border2}`,
                    background: isBest ? C.green10+"55" : "transparent",
                  }}>
                    <td style={{padding:"11px 8px"}}>
                      <span style={{display:"flex",alignItems:"center",gap:8}}>
                        <div style={{width:9,height:9,borderRadius:5,background:p.color,flexShrink:0}}/>
                        <span style={{fontWeight:600,color:C.text}}>{p.name}</span>
                        {isBest && (
                          <span style={{fontSize:9,fontWeight:700,color:C.greenDark,background:C.green10,padding:"2px 6px",borderRadius:8,letterSpacing:.4}}>{tr("analysis.platforms.badgeBest")}</span>
                        )}
                      </span>
                    </td>
                    <td className="num" style={{padding:"11px 8px",textAlign:"right",color:C.muted,fontVariantNumeric:"tabular-nums"}}>{p.entries}</td>
                    <td className="num" style={{padding:"11px 8px",textAlign:"right",color:C.text,fontVariantNumeric:"tabular-nums",fontWeight:500}}>{sym}{p.gross.toFixed(0)}</td>
                    <td className="num" style={{padding:"11px 8px",textAlign:"right",color:C.text,fontVariantNumeric:"tabular-nums"}}>{sym}{p.net.toFixed(0)}</td>
                    <td style={{padding:"11px 8px",textAlign:"right",fontVariantNumeric:"tabular-nums"}}>
                      <div style={{color:C.text}}>{sym}{p.commission.toFixed(0)}</div>
                      <div style={{fontSize:10,color:p.commissionPct>0.32?C.red:p.commissionPct>0.30?C.yellow:C.muted2,fontWeight:600,marginTop:2}}>{(p.commissionPct*100).toFixed(1)}%</div>
                    </td>
                    <td style={{padding:"11px 8px",textAlign:"right",fontVariantNumeric:"tabular-nums"}}>
                      <div style={{color:C.text}}>{sym}{p.cpc.toFixed(0)}</div>
                      <div style={{fontSize:10,color:p.cpcPctOfNet>0.08?C.red:p.cpcPctOfNet>0.06?C.yellow:C.muted2,fontWeight:600,marginTop:2}}>{(p.cpcPctOfNet*100).toFixed(1)}%</div>
                    </td>
                    <td className="num" style={{padding:"11px 8px",textAlign:"right",color:C.text,fontVariantNumeric:"tabular-nums"}}>{sym}{p.cogs.toFixed(0)}</td>
                    <td className="num" style={{padding:"11px 8px",textAlign:"right",fontVariantNumeric:"tabular-nums",fontWeight:700,color:posc(p.profit)}}>{sym}{p.profit.toFixed(0)}</td>
                    <td style={{padding:"11px 8px",textAlign:"right"}}>
                      <span style={{
                        display:"inline-block",
                        padding:"3px 9px",
                        borderRadius:12,
                        fontSize:11,
                        fontWeight:700,
                        background:p.profitPct >= 0.35 ? C.green10 : p.profitPct >= 0.25 ? "#e8f4fd" : p.profitPct > 0 ? C.yellow10 : C.red10,
                        color:p.profitPct >= 0.35 ? C.greenDark : p.profitPct >= 0.25 ? C.blueDark : p.profitPct > 0 ? "#7a4f00" : C.redDark
                      }}>
                        {(p.profitPct*100).toFixed(1)}%
                      </span>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {perPlatform.length > 1 && (
            <div style={{display:"flex",gap:14,marginTop:14,padding:"10px 14px",background:C.bg3,borderRadius:7,fontSize:11,color:C.muted,flexWrap:"wrap"}}>
              <span><span style={{color:C.greenDark,fontWeight:700}}>{tr("analysis.legend.excellent")}</span> ≥ 35%</span>
              <span><span style={{color:C.blueDark,fontWeight:700}}>{tr("analysis.legend.healthy")}</span> 25–34%</span>
              <span><span style={{color:"#7a4f00",fontWeight:700}}>{tr("analysis.legend.marginal")}</span> &lt; 25%</span>
              <span><span style={{color:C.redDark,fontWeight:700}}>{tr("analysis.legend.loss")}</span> &lt; 0%</span>
            </div>
          )}
        </Card>
      )}

      {/* Highlights */}
      <Card sx={{marginBottom:16}}>
        <div style={{fontSize:14,fontWeight:700,color:C.text,marginBottom:14,letterSpacing:"-.2px"}}>{tr("analysis.highlights.title")}</div>
        <div className="nv-grid-1-sm" style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:12}}>
          {bestDay && (
            <div style={{padding:"14px 16px",background:"#fff",border:`1px solid ${C.green}33`,borderLeft:`3px solid ${C.green}`,borderRadius:8}}>
              <div style={{fontSize:10,color:C.greenDark,fontWeight:700,textTransform:"uppercase",letterSpacing:.6,marginBottom:6}}>{tr("analysis.highlights.bestDay")}</div>
              <div className="num" style={{fontSize:16,fontWeight:700,color:C.greenDark,letterSpacing:"-.2px"}}>{sym}{(bestDay.trueProfit||0).toFixed(2)}</div>
              <div style={{fontSize:11.5,color:C.muted,marginTop:4,fontWeight:500}}>{bestDay.dateObj?.toLocaleDateString("en-GB")} · {bestDay.platform}</div>
            </div>
          )}
          {worstDay && worstDay !== bestDay && (
            <div style={{padding:"14px 16px",background:"#fff",border:`1px solid ${C.red}33`,borderLeft:`3px solid ${C.red}`,borderRadius:8}}>
              <div style={{fontSize:10,color:C.redDark,fontWeight:700,textTransform:"uppercase",letterSpacing:.6,marginBottom:6}}>{tr("analysis.highlights.weakestDay")}</div>
              <div className="num" style={{fontSize:16,fontWeight:700,color:C.redDark,letterSpacing:"-.2px"}}>{sym}{(worstDay.trueProfit||0).toFixed(2)}</div>
              <div style={{fontSize:11.5,color:C.muted,marginTop:4,fontWeight:500}}>{worstDay.dateObj?.toLocaleDateString("en-GB")} · {worstDay.platform}</div>
            </div>
          )}
          {bestPlatform && (
            <div style={{padding:"14px 16px",background:"#fff",border:`1px solid ${C.blue}33`,borderLeft:`3px solid ${C.blue}`,borderRadius:8}}>
              <div style={{fontSize:10,color:C.blueDark,fontWeight:700,textTransform:"uppercase",letterSpacing:.6,marginBottom:6}}>{tr("analysis.highlights.topPlatform")}</div>
              <div style={{fontSize:16,fontWeight:700,color:C.blueDark,letterSpacing:"-.2px"}}>{bestPlatform.name}</div>
              <div style={{fontSize:11.5,color:C.muted,marginTop:4,fontWeight:500}}>{(bestPlatform.profitPct*100).toFixed(1)}%{tr("analysis.highlights.marginSuffix")} · {sym}{bestPlatform.profit.toFixed(0)}{tr("analysis.highlights.profitSuffix")}</div>
            </div>
          )}
          {worstPlatform && worstPlatform.profit <= 0 && (
            <div style={{padding:"14px 16px",background:"#fff",border:`1px solid ${C.red}33`,borderLeft:`3px solid ${C.red}`,borderRadius:8}}>
              <div style={{fontSize:10,color:C.redDark,fontWeight:700,textTransform:"uppercase",letterSpacing:.6,marginBottom:6}}>{tr("analysis.highlights.underperforming")}</div>
              <div style={{fontSize:16,fontWeight:700,color:C.redDark,letterSpacing:"-.2px"}}>{worstPlatform.name}</div>
              <div style={{fontSize:11.5,color:C.muted,marginTop:4,fontWeight:500}}>{worstPlatform.profit < 0 ? tr("analysis.highlights.losingMoney") : tr("analysis.highlights.breakeven")} · {sym}{worstPlatform.profit.toFixed(0)}{tr("analysis.highlights.profitSuffix")}</div>
            </div>
          )}
        </div>
        {flags.length > 0 && (
          <div style={{marginTop:16,paddingTop:16,borderTop:`1px solid ${C.border2}`}}>
            <div style={{fontSize:11,color:C.muted,fontWeight:700,textTransform:"uppercase",letterSpacing:.6,marginBottom:10}}>{tr("analysis.flags.headingPrefix")} ({flags.length})</div>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {flags.map((f,i) => (
                <div key={i} style={{padding:"12px 14px",background:"#fff",border:`1px solid ${C.yellow}55`,borderLeft:`3px solid ${C.yellow}`,borderRadius:7}}>
                  <div style={{fontSize:13,fontWeight:700,color:"#7a4f00",marginBottom:3,letterSpacing:"-.1px"}}>{f.title}</div>
                  <div style={{fontSize:12,color:"#7a4f00",lineHeight:1.55,fontWeight:500}}>{f.text}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* AI Insights moved to top */}
    </div>
  );
}
